import React, { useCallback, useEffect, useState, useRef, Fragment } from 'react';
import { motion } from 'framer-motion';
import { SunIcon, MoonIcon, StarIcon, SparklesIcon, CalendarIcon, ClockIcon, MapPinIcon, HeartIcon, BriefcaseIcon, CompassIcon, ShieldIcon, ZapIcon, TrendingUpIcon, BookOpenIcon, TargetIcon, CheckIcon, ActivityIcon, LayersIcon, UsersIcon, AlertTriangleIcon, MessageCircleIcon } from 'lucide-react';
import { Badge } from '../ui/Badge';
import { Card } from '../ui/Card';
import { SOLAR_INTRO, SOLAR_YEAR_THEME, SOLAR_ASCENDANT, SOLAR_SUN_POSITION, SOLAR_MOON, SOLAR_PLANETS_IN_HOUSES, SOLAR_ASPECTS, SOLAR_LIFE_AREAS, SOLAR_RECOMMENDATIONS, SOLAR_SUMMARY } from '../../data/solarReportContent';
import { COMPAT_INTRO, COMPAT_OVERALL_DYNAMICS, COMPAT_SUN_INTERACTION, COMPAT_EMOTIONAL, COMPAT_ROMANTIC, COMPAT_INTELLECTUAL, COMPAT_KARMIC, COMPAT_PLANETS_IN_HOUSES, COMPAT_ASPECTS_DETAILED, COMPAT_STRENGTHS, COMPAT_CONFLICTS, COMPAT_LONGTERM, COMPAT_RECOMMENDATIONS, COMPAT_SUMMARY } from '../../data/compatibilityReportContent';
import { COMBO_NATAL_INTRO, COMBO_PERSONALITY, COMBO_SUN, COMBO_MOON, COMBO_ASCENDANT, COMBO_PLANETS_IN_SIGNS, COMBO_HOUSES_SUMMARY, COMBO_NATAL_ASPECTS, COMBO_NATAL_LIFE_AREAS, COMBO_SOLAR_INTRO, COMBO_YEAR_ENERGY, COMBO_SOLAR_ASCENDANT, COMBO_SOLAR_SUN, COMBO_SOLAR_MOON, COMBO_SOLAR_PLANETS, COMBO_SOLAR_ASPECTS, COMBO_KEY_PERIODS, COMBO_SOLAR_NATAL_INTERACTION, COMBO_YEAR_LIFE_AREAS, COMBO_RECOMMENDATIONS, COMBO_FINAL_SUMMARY } from '../../data/comboReportContent';
import { FORECAST_INTRO, FORECAST_PERIOD_ENERGY, FORECAST_SLOW_TRANSITS, FORECAST_PERSONAL_TRANSITS, FORECAST_HOUSES_ACTIVATION, FORECAST_TRANSIT_ASPECTS, FORECAST_KEY_PERIODS, FORECAST_LIFE_AREAS, FORECAST_OPPORTUNITIES, FORECAST_DIFFICULTIES, FORECAST_RECOMMENDATIONS, FORECAST_SUMMARY, FORECAST_MONTHLY_DETAIL } from '../../data/forecastReportContent';
import { INTRO_CONTENT, EXTENDED_SUN, EXTENDED_MOON, EXTENDED_MERCURY, EXTENDED_VENUS, EXTENDED_MARS, EXTENDED_JUPITER, EXTENDED_SATURN, OUTER_PLANETS_EXTENDED, HOUSES_DETAILED, ASPECTS_DETAILED, HEALTH_SECTION, KARMIC_SECTION, TALENTS_SECTION, CHALLENGES_SECTION, FORECAST_SECTION, FINAL_ANALYSIS, PLANET_POSITIONS_TABLE, ENERGY_RADAR_DATA, ZODIAC_COMPATIBILITY, PERSONAL_AFFIRMATIONS, CURRENT_TRANSITS, CELEBRITY_MATCHES } from '../../data/fullReportContent';
type ServiceType = 'natal' | 'solar' | 'compatibility' | 'combo' | 'forecast';
interface PremiumReportPreviewProps {
  serviceType: ServiceType;
}
// ═══════════════════════════════════════════
// SERVICE-SPECIFIC DATA
// ═══════════════════════════════════════════
const SERVICE_CONFIG: Record<ServiceType, {
  title: string;
  subtitle: string;
  gradient: string;
  accentColor: string;
  badgeClass: string;
  icon: React.ReactNode;
  person2?: {
    name: string;
    date: string;
    time: string;
    place: string;
  };
  extraInfo?: string;
}> = {
  natal: {
    title: 'Натальная карта',
    subtitle: 'Полный астрологический разбор личности',
    gradient: 'from-slate-900 via-indigo-900 to-slate-900',
    accentColor: 'indigo',
    badgeClass: 'bg-indigo-500/30 text-indigo-200 border-indigo-400/30',
    icon: <StarIcon className="w-8 h-8" data-id="element-17192" />
  },
  solar: {
    title: 'Соляр (Прогноз на год)',
    subtitle: 'Персональный астрологический прогноз на 2026 год',
    gradient: 'from-slate-900 via-amber-900 to-slate-900',
    accentColor: 'amber',
    badgeClass: 'bg-amber-500/30 text-amber-200 border-amber-400/30',
    icon: <SunIcon className="w-8 h-8" data-id="element-17193" />,
    extraInfo: 'Солярный год: 2026'
  },
  compatibility: {
    title: 'Совместимость (Синастрия)',
    subtitle: 'Астрологический анализ отношений',
    gradient: 'from-slate-900 via-rose-900 to-slate-900',
    accentColor: 'rose',
    badgeClass: 'bg-rose-500/30 text-rose-200 border-rose-400/30',
    icon: <HeartIcon className="w-8 h-8" data-id="element-17194" />,
    person2: {
      name: 'Мария Иванова',
      date: '23 марта 1994',
      time: '09:15',
      place: 'Санкт-Петербург'
    }
  },
  combo: {
    title: 'Комбо: Натал + Соляр',
    subtitle: 'Полный анализ личности + прогноз на год',
    gradient: 'from-slate-900 via-purple-900 to-slate-900',
    accentColor: 'purple',
    badgeClass: 'bg-purple-500/30 text-purple-200 border-purple-400/30',
    icon: <LayersIcon className="w-8 h-8" data-id="element-17195" />,
    extraInfo: 'Солярный год: 2026'
  },
  forecast: {
    title: 'Прогноз (Транзиты)',
    subtitle: 'Анализ текущих и предстоящих транзитов',
    gradient: 'from-slate-900 via-teal-900 to-slate-900',
    accentColor: 'teal',
    badgeClass: 'bg-teal-500/30 text-teal-200 border-teal-400/30',
    icon: <ActivityIcon className="w-8 h-8" data-id="element-17196" />,
    extraInfo: 'Период: Март 2026 — Сентябрь 2026'
  }
};
const REPORT_PAGE_COUNT: Record<ServiceType, number> = {
  natal: 35,
  solar: 38,
  compatibility: 32,
  combo: 62,
  forecast: 30
};
const TOC_BY_SERVICE: Record<ServiceType, {
  num: string;
  title: string;
}[]> = {
  natal: [{
    num: '01',
    title: 'Введение'
  }, {
    num: '02',
    title: 'Общий психологический портрет'
  }, {
    num: '03',
    title: 'Солнце — Ядро личности'
  }, {
    num: '04',
    title: 'Луна — Внутренний мир'
  }, {
    num: '05',
    title: 'Асцендент — Социальная маска'
  }, {
    num: '06',
    title: 'Меркурий — Мышление'
  }, {
    num: '07',
    title: 'Венера — Любовь и ценности'
  }, {
    num: '08',
    title: 'Марс — Энергия и действие'
  }, {
    num: '09',
    title: 'Планеты в домах'
  }, {
    num: '10',
    title: 'Аспекты между планетами'
  }, {
    num: '11',
    title: 'Карьера и финансы'
  }, {
    num: '12',
    title: 'Любовь и отношения'
  }, {
    num: '13',
    title: 'Кармические задачи'
  }, {
    num: '14',
    title: 'Таланты и предназначение'
  }, {
    num: '15',
    title: 'Итоговый анализ'
  }],
  solar: [{
    num: '01',
    title: 'Введение'
  }, {
    num: '02',
    title: 'Общая тема года'
  }, {
    num: '03',
    title: 'Асцендент соляра'
  }, {
    num: '04',
    title: 'Положение Солнца в соляре'
  }, {
    num: '05',
    title: 'Луна в соляре'
  }, {
    num: '06',
    title: 'Меркурий в соляре'
  }, {
    num: '07',
    title: 'Венера в соляре'
  }, {
    num: '08',
    title: 'Марс в соляре'
  }, {
    num: '09',
    title: 'Юпитер, Сатурн и внешние планеты'
  }, {
    num: '10',
    title: 'Аспекты соляра'
  }, {
    num: '11',
    title: 'Карьера и финансы'
  }, {
    num: '12',
    title: 'Отношения и семья'
  }, {
    num: '13',
    title: 'Личностное развитие и здоровье'
  }, {
    num: '14',
    title: 'Важные периоды года'
  }, {
    num: '15',
    title: 'Рекомендации на год'
  }, {
    num: '16',
    title: 'Итог прогноза'
  }],
  compatibility: [{
    num: '01',
    title: 'Введение'
  }, {
    num: '02',
    title: 'Общая динамика отношений'
  }, {
    num: '03',
    title: 'Солнце и личность партнёров'
  }, {
    num: '04',
    title: 'Эмоциональная совместимость'
  }, {
    num: '05',
    title: 'Романтическая и сексуальная совместимость'
  }, {
    num: '06',
    title: 'Интеллектуальная совместимость'
  }, {
    num: '07',
    title: 'Кармические связи'
  }, {
    num: '08',
    title: 'Планеты партнёра в домах другого'
  }, {
    num: '09',
    title: 'Основные аспекты между картами'
  }, {
    num: '10',
    title: 'Сильные стороны отношений'
  }, {
    num: '11',
    title: 'Возможные конфликты'
  }, {
    num: '12',
    title: 'Потенциал долгосрочных отношений'
  }, {
    num: '13',
    title: 'Практические рекомендации'
  }, {
    num: '14',
    title: 'Итог анализа'
  }],
  combo: [{
    num: '01',
    title: 'Введение'
  }, {
    num: '02',
    title: 'Общая характеристика личности'
  }, {
    num: '03',
    title: 'Солнце — Ядро личности'
  }, {
    num: '04',
    title: 'Луна — Эмоциональная природа'
  }, {
    num: '05',
    title: 'Асцендент — Внешнее проявление'
  }, {
    num: '06',
    title: 'Планеты в знаках'
  }, {
    num: '07',
    title: 'Планеты в домах'
  }, {
    num: '08',
    title: 'Аспекты натальной карты'
  }, {
    num: '09',
    title: 'Сферы жизни (натал)'
  }, {
    num: '10',
    title: 'Введение в соляр'
  }, {
    num: '11',
    title: 'Общая энергия 2026 года'
  }, {
    num: '12',
    title: 'Асцендент соляра'
  }, {
    num: '13',
    title: 'Солнце в соляре'
  }, {
    num: '14',
    title: 'Луна в соляре'
  }, {
    num: '15',
    title: 'Планеты соляра в домах'
  }, {
    num: '16',
    title: 'Аспекты соляра'
  }, {
    num: '17',
    title: 'Важные периоды года'
  }, {
    num: '18',
    title: 'Влияние соляра на натал'
  }, {
    num: '19',
    title: 'Сферы жизни (год)'
  }, {
    num: '20',
    title: 'Рекомендации на год'
  }, {
    num: '21',
    title: 'Итоговый анализ'
  }],
  forecast: [{
    num: '01',
    title: 'Введение'
  }, {
    num: '02',
    title: 'Общая энергия периода'
  }, {
    num: '03',
    title: 'Медленные транзиты'
  }, {
    num: '04',
    title: 'Транзиты личных планет'
  }, {
    num: '05',
    title: 'Транзиты по домам'
  }, {
    num: '06',
    title: 'Аспекты транзитов'
  }, {
    num: '07',
    title: 'Ключевые периоды'
  }, {
    num: '08',
    title: 'Сферы жизни'
  }, {
    num: '09',
    title: 'Возможности периода'
  }, {
    num: '10',
    title: 'Потенциальные трудности'
  }, {
    num: '11',
    title: 'Рекомендации'
  }, {
    num: '12',
    title: 'Итог прогноза'
  }]
};
// ═══════════════════════════════════════════
// NATAL DATA
// ═══════════════════════════════════════════
const NATAL_ELEMENT_BALANCE = [{
  element: 'Огонь',
  percent: 20,
  color: 'bg-red-500',
  planets: 'Солнце, Меркурий'
}, {
  element: 'Земля',
  percent: 30,
  color: 'bg-emerald-500',
  planets: 'Юпитер, Уран, Нептун'
}, {
  element: 'Воздух',
  percent: 20,
  color: 'bg-blue-500',
  planets: 'Марс, Сатурн'
}, {
  element: 'Вода',
  percent: 30,
  color: 'bg-indigo-500',
  planets: 'Луна, Венера, Плутон'
}];
const NATAL_ASPECTS_GRID = [{
  planets: '☉ ☌ ☿',
  type: 'Соединение',
  nature: 'intense'
}, {
  planets: '☽ △ ♀',
  type: 'Трин',
  nature: 'harmonious'
}, {
  planets: '♀ ⚹ ♃',
  type: 'Секстиль',
  nature: 'harmonious'
}, {
  planets: '♂ □ ♃',
  type: 'Квадрат',
  nature: 'tense'
}, {
  planets: '♂ □ ♄',
  type: 'Квадрат',
  nature: 'tense'
}];
// ═══════════════════════════════════════════
// SOLAR DATA
// ═══════════════════════════════════════════
const SOLAR_MONTHLY_FORECAST = [{
  month: 'Янв',
  intensity: 60,
  theme: 'Планирование'
}, {
  month: 'Фев',
  intensity: 55,
  theme: 'Подготовка'
}, {
  month: 'Мар',
  intensity: 90,
  theme: 'Прорыв'
}, {
  month: 'Апр',
  intensity: 85,
  theme: 'Рост'
}, {
  month: 'Май',
  intensity: 80,
  theme: 'Реализация'
}, {
  month: 'Июн',
  intensity: 65,
  theme: 'Стабилизация'
}, {
  month: 'Июл',
  intensity: 50,
  theme: 'Отдых'
}, {
  month: 'Авг',
  intensity: 70,
  theme: 'Новый цикл'
}, {
  month: 'Сен',
  intensity: 75,
  theme: 'Активность'
}, {
  month: 'Окт',
  intensity: 60,
  theme: 'Завершение'
}, {
  month: 'Ноя',
  intensity: 55,
  theme: 'Рефлексия'
}, {
  month: 'Дек',
  intensity: 65,
  theme: 'Итоги'
}];
const SOLAR_KEY_DATES = [{
  date: '15 марта',
  event: 'Новолуние в 10 доме',
  type: 'positive'
}, {
  date: '28 апреля',
  event: 'Юпитер тригон Солнце',
  type: 'positive'
}, {
  date: '12 июня',
  event: 'Сатурн квадрат Луна',
  type: 'caution'
}, {
  date: '8 августа',
  event: 'День рождения — новый соляр',
  type: 'neutral'
}, {
  date: '22 октября',
  event: 'Затмение в 5 доме',
  type: 'caution'
}, {
  date: '5 декабря',
  event: 'Венера секстиль Юпитер',
  type: 'positive'
}];
// ═══════════════════════════════════════════
// COMPATIBILITY DATA
// ═══════════════════════════════════════════
const COMPATIBILITY_SCORES = [{
  area: 'Эмоциональная связь',
  percent: 85,
  color: 'bg-rose-500'
}, {
  area: 'Романтика и страсть',
  percent: 92,
  color: 'bg-pink-500'
}, {
  area: 'Коммуникация',
  percent: 78,
  color: 'bg-sky-500'
}, {
  area: 'Быт и финансы',
  percent: 70,
  color: 'bg-emerald-500'
}, {
  area: 'Долгосрочные перспективы',
  percent: 88,
  color: 'bg-purple-500'
}];
const COMPATIBILITY_ASPECTS = [{
  aspect: '☉ △ ☽',
  desc: 'Солнце — Луна',
  nature: 'harmonious'
}, {
  aspect: '♀ ☌ ♂',
  desc: 'Венера — Марс',
  nature: 'intense'
}, {
  aspect: '☽ ⚹ ☽',
  desc: 'Луна — Луна',
  nature: 'harmonious'
}, {
  aspect: '☿ □ ☿',
  desc: 'Меркурий — Меркурий',
  nature: 'tense'
}, {
  aspect: '♃ △ ♀',
  desc: 'Юпитер — Венера',
  nature: 'harmonious'
}];
// ═══════════════════════════════════════════
// FORECAST DATA
// ═══════════════════════════════════════════
const FORECAST_ACTIVE_TRANSITS = [{
  planet: 'Юпитер',
  position: 'в 10 доме',
  effect: 'Карьерный рост',
  strength: 90,
  color: 'bg-amber-500'
}, {
  planet: 'Сатурн',
  position: 'в 7 доме',
  effect: 'Уроки в отношениях',
  strength: 75,
  color: 'bg-slate-500'
}, {
  planet: 'Уран',
  position: 'в 1 доме',
  effect: 'Личная трансформация',
  strength: 85,
  color: 'bg-cyan-500'
}, {
  planet: 'Нептун',
  position: 'в 9 доме',
  effect: 'Духовный поиск',
  strength: 60,
  color: 'bg-indigo-500'
}, {
  planet: 'Плутон',
  position: 'в 8 доме',
  effect: 'Глубокие перемены',
  strength: 70,
  color: 'bg-purple-500'
}];
const FORECAST_TIMELINE = [{
  period: 'Март',
  events: ['Юпитер тригон Солнце', 'Новолуние в 10 доме'],
  intensity: 'high'
}, {
  period: 'Апрель',
  events: ['Меркурий ретро', 'Венера в 7 доме'],
  intensity: 'medium'
}, {
  period: 'Май',
  events: ['Затмение в 11 доме', 'Марс секстиль Юпитер'],
  intensity: 'high'
}, {
  period: 'Июнь',
  events: ['Сатурн квадрат Луна'],
  intensity: 'low'
}];
export function PremiumReportPreview({
  serviceType
}: PremiumReportPreviewProps) {
  const config = SERVICE_CONFIG[serviceType];
  const toc = TOC_BY_SERVICE[serviceType];
  // Review state
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  const [reviewTags, setReviewTags] = useState<string[]>([]);
  const [reviewText, setReviewText] = useState('');
  const handlePrint = useCallback(() => {
    window.print();
  }, []);
  // ═══════════════════════════════════════════
  // RENDER NATAL CONTENT
  // ═══════════════════════════════════════════
  const renderNatalContent = () => {
    const SectionHeader = ({
      icon,
      title,
      subtitle,
      color = 'indigo'
    }: {
      icon: React.ReactNode;
      title: string;
      subtitle?: string;
      color?: string;
    }) => <div className="flex items-center gap-3 mb-4 sm:mb-6" data-id="element-17197">
        <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-${color}-100 text-${color}-600 flex items-center justify-center`} data-id="element-17198">
          {icon}
        </div>
        <div className="min-w-0" data-id="element-17199">
          <h2 className="text-lg sm:text-xl font-bold text-slate-900 leading-tight" data-id="element-17200">
            {title}
          </h2>
          {subtitle && <p className="text-xs sm:text-sm text-slate-500" data-id="element-17201">{subtitle}</p>}
        </div>
      </div>;
    const renderParagraphs = (text: string) => {
      return text.split('\n\n').map((paragraph, i) => <p key={i} className="text-sm sm:text-base text-slate-600 leading-relaxed mb-3 sm:mb-4 last:mb-0" data-id="element-17202">
          {paragraph}
        </p>);
    };
    return <>
        {/* 01. ВВЕДЕНИЕ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-17203">
          <SectionHeader icon={<BookOpenIcon className="w-5 h-5" data-id="element-17205" />} title={INTRO_CONTENT.title} subtitle="Что такое натальная карта и как работает астрология" data-id="element-17204" />
          <div className="space-y-6" data-id="element-17206">
            <div data-id="element-17207">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-17208">
                Что такое натальная карта?
              </h3>
              {renderParagraphs(INTRO_CONTENT.whatIsNatalChart)}
            </div>
            <div data-id="element-17209">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-17210">
                Как это работает?
              </h3>
              {renderParagraphs(INTRO_CONTENT.howItWorks)}
            </div>
            <div className="bg-indigo-50/50 p-5 rounded-xl border border-indigo-100" data-id="element-17211">
              <h3 className="font-bold text-indigo-800 mb-2" data-id="element-17212">
                Как читать этот отчёт
              </h3>
              {renderParagraphs(INTRO_CONTENT.howToRead)}
            </div>
          </div>
        </div>

        {/* 02. ОБЩИЙ ПСИХОЛОГИЧЕСКИЙ ПОРТРЕТ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-indigo-50/30 to-white" data-id="element-17213">
          <SectionHeader icon={<SparklesIcon className="w-5 h-5" data-id="element-17215" />} title="Общий психологический портрет" subtitle="Фундамент вашей личности" data-id="element-17214" />

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 mb-8" data-id="element-17216">
            <Card padding="md" className="bg-white border-amber-200 shadow-sm" data-id="element-17217">
              <div className="flex items-center gap-2 mb-3" data-id="element-17218">
                <SunIcon className="w-5 h-5 text-amber-500" data-id="element-17219" />
                <span className="text-xs font-bold text-amber-600 uppercase tracking-wider" data-id="element-17220">
                  Солнце (Эго)
                </span>
              </div>
              <div className="text-2xl font-bold text-slate-900 flex items-center gap-2" data-id="element-17221">
                <span className="text-amber-500" data-id="element-17222">♌</span> Лев
              </div>
              <p className="text-xs text-slate-500 mt-2" data-id="element-17223">
                Обитель Солнца • Огненная стихия
              </p>
            </Card>

            <Card padding="md" className="bg-white border-indigo-200 shadow-sm" data-id="element-17224">
              <div className="flex items-center gap-2 mb-3" data-id="element-17225">
                <MoonIcon className="w-5 h-5 text-indigo-500" data-id="element-17226" />
                <span className="text-xs font-bold text-indigo-600 uppercase tracking-wider" data-id="element-17227">
                  Луна (Душа)
                </span>
              </div>
              <div className="text-2xl font-bold text-slate-900 flex items-center gap-2" data-id="element-17228">
                <span className="text-indigo-500" data-id="element-17229">♏</span> Скорпион
              </div>
              <p className="text-xs text-slate-500 mt-2" data-id="element-17230">
                Падение Луны • Водная стихия
              </p>
            </Card>

            <Card padding="md" className="bg-white border-purple-200 shadow-sm" data-id="element-17231">
              <div className="flex items-center gap-2 mb-3" data-id="element-17232">
                <StarIcon className="w-5 h-5 text-purple-500" data-id="element-17233" />
                <span className="text-xs font-bold text-purple-600 uppercase tracking-wider" data-id="element-17234">
                  Асцендент (Маска)
                </span>
              </div>
              <div className="text-2xl font-bold text-slate-900 flex items-center gap-2" data-id="element-17235">
                <span className="text-purple-500" data-id="element-17236">♐</span> Стрелец
              </div>
              <p className="text-xs text-slate-500 mt-2" data-id="element-17237">
                Управитель: Юпитер • Огненная стихия
              </p>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8" data-id="element-17238">
            <div data-id="element-17239">
              <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider mb-4" data-id="element-17240">
                Баланс стихий
              </h3>
              <div className="space-y-4" data-id="element-17241">
                {NATAL_ELEMENT_BALANCE.map((item, i) => <div key={i} data-id="element-17242">
                    <div className="flex justify-between text-sm mb-1.5" data-id="element-17243">
                      <span className="font-medium text-slate-700" data-id="element-17244">
                        {item.element} ({item.percent}%)
                      </span>
                      <span className="text-slate-400 text-xs" data-id="element-17245">
                        {item.planets}
                      </span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-2.5 overflow-hidden" data-id="element-17246">
                      <motion.div className={`${item.color} h-2.5 rounded-full`} initial={{
                    width: 0
                  }} animate={{
                    width: `${item.percent}%`
                  }} transition={{
                    duration: 1,
                    delay: i * 0.15,
                    ease: 'easeOut'
                  }} data-id="element-17247" />
                    </div>
                  </div>)}
              </div>
            </div>

            <div data-id="element-17248">
              <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider mb-4" data-id="element-17249">
                Энергетический профиль
              </h3>
              <div className="space-y-4" data-id="element-17250">
                {ENERGY_RADAR_DATA.axes.map((item, i) => <div key={i} data-id="element-17251">
                    <div className="flex justify-between text-sm mb-1.5" data-id="element-17252">
                      <span className="font-medium text-slate-700" data-id="element-17253">
                        {item.label}
                      </span>
                      <span className="font-bold text-indigo-600" data-id="element-17254">
                        {item.value}/100
                      </span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-2.5 overflow-hidden" data-id="element-17255">
                      <motion.div className="bg-indigo-500 h-2.5 rounded-full" initial={{
                    width: 0
                  }} animate={{
                    width: `${item.value}%`
                  }} transition={{
                    duration: 1,
                    delay: i * 0.1,
                    ease: 'easeOut'
                  }} data-id="element-17256" />
                    </div>
                  </div>)}
              </div>
            </div>
          </div>

          <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider mb-4" data-id="element-17257">
            Положения планет
          </h3>
          <div className="-mx-4 sm:mx-0 overflow-x-auto" data-id="element-17258">
            <table className="w-full text-sm text-left border-collapse min-w-[500px]" data-id="element-17259">
              <thead className="bg-slate-50 text-slate-500 font-medium" data-id="element-17260">
                <tr data-id="element-17261">
                  <th className="px-4 py-3 rounded-tl-lg" data-id="element-17262">Планета</th>
                  <th className="px-4 py-3" data-id="element-17263">Знак</th>
                  <th className="px-4 py-3" data-id="element-17264">Градус</th>
                  <th className="px-4 py-3" data-id="element-17265">Дом</th>
                  <th className="px-4 py-3 rounded-tr-lg" data-id="element-17266">Достоинство</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white" data-id="element-17267">
                {PLANET_POSITIONS_TABLE.map((planet, i) => <tr key={i} className="hover:bg-slate-50/50 transition-colors" data-id="element-17268">
                    <td className="px-4 py-3 font-medium text-slate-900 flex items-center gap-2" data-id="element-17269">
                      <span className="text-lg text-indigo-500 w-6 text-center" data-id="element-17270">
                        {planet.symbol}
                      </span>
                      {planet.planet}
                    </td>
                    <td className="px-4 py-3 text-slate-700" data-id="element-17271">{planet.sign}</td>
                    <td className="px-4 py-3 text-slate-500 font-mono text-xs" data-id="element-17272">
                      {planet.degree}
                    </td>
                    <td className="px-4 py-3 text-slate-700" data-id="element-17273">{planet.house}</td>
                    <td className="px-4 py-3 text-slate-500" data-id="element-17274">
                      {planet.dignity}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>

        {/* 03. СОЛНЦЕ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-17275">
          <SectionHeader icon={<SunIcon className="w-5 h-5" data-id="element-17277" />} title="Солнце во Льве" subtitle="Ядро личности, эго и жизненная сила" color="amber" data-id="element-17276" />
          <div className="space-y-6" data-id="element-17278">
            <div data-id="element-17279">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-17280">Жизненная цель</h3>
              {renderParagraphs(EXTENDED_SUN.lifePurpose)}
            </div>
            <div data-id="element-17281">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-17282">
                В повседневной жизни
              </h3>
              {renderParagraphs(EXTENDED_SUN.dailyLife)}
            </div>
            <div data-id="element-17283">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-17284">В отношениях</h3>
              {renderParagraphs(EXTENDED_SUN.inRelationships)}
            </div>
          </div>
        </div>

        {/* 04. ЛУНА */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-indigo-50/30 to-white" data-id="element-17285">
          <SectionHeader icon={<MoonIcon className="w-5 h-5" data-id="element-17287" />} title="Луна в Скорпионе" subtitle="Внутренний мир, эмоции и подсознание" color="indigo" data-id="element-17286" />
          <div className="space-y-6" data-id="element-17288">
            <div data-id="element-17289">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-17290">
                Эмоциональные паттерны
              </h3>
              {renderParagraphs(EXTENDED_MOON.emotionalPatterns)}
            </div>
            <div data-id="element-17291">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-17292">
                Отношения с матерью
              </h3>
              {renderParagraphs(EXTENDED_MOON.motherRelation)}
            </div>
            <div className="bg-indigo-50/50 p-5 rounded-xl border border-indigo-100" data-id="element-17293">
              <h3 className="font-bold text-indigo-800 mb-2" data-id="element-17294">
                Скрытые потребности
              </h3>
              {renderParagraphs(EXTENDED_MOON.hiddenNeeds)}
            </div>
          </div>
        </div>

        {/* 05. АСЦЕНДЕНТ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-17295">
          <SectionHeader icon={<StarIcon className="w-5 h-5" data-id="element-17297" />} title="Асцендент в Стрельце" subtitle="Социальная маска и первое впечатление" color="purple" data-id="element-17296" />
          <div className="space-y-4" data-id="element-17298">
            <p className="text-slate-600 leading-relaxed" data-id="element-17299">
              Асцендент в Стрельце формирует ваш внешний образ — оптимистичный,
              открытый и любознательный. Люди воспринимают вас как человека с
              широким кругозором и философским взглядом на жизнь. Вы производите
              впечатление человека, который знает, куда идёт, и приглашает
              других присоединиться к путешествию.
            </p>
            <p className="text-slate-600 leading-relaxed" data-id="element-17300">
              Ваше тело, скорее всего, крупное или высокое, с широкими жестами и
              открытой позой. Вы излучаете энтузиазм и уверенность. Однако за
              этой жизнерадостной маской скрывается глубокий и интенсивный
              внутренний мир (Луна в Скорпионе), который вы открываете далеко не
              всем.
            </p>
          </div>
        </div>

        {/* 06. МЕРКУРИЙ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-sky-50/30 to-white" data-id="element-17301">
          <SectionHeader icon={<MessageCircleIcon className="w-5 h-5" data-id="element-17303" />} title="Меркурий во Льве" subtitle="Мышление, речь и коммуникация" color="sky" data-id="element-17302" />
          <div className="space-y-6" data-id="element-17304">
            <div data-id="element-17305">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-17306">Стиль обучения</h3>
              {renderParagraphs(EXTENDED_MERCURY.learningStyle)}
            </div>
            <div data-id="element-17307">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-17308">Стиль общения</h3>
              {renderParagraphs(EXTENDED_MERCURY.communicationStyle)}
            </div>
            <div data-id="element-17309">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-17310">
                Принятие решений
              </h3>
              {renderParagraphs(EXTENDED_MERCURY.decisionMaking)}
            </div>
          </div>
        </div>

        {/* 07. ВЕНЕРА */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-17311">
          <SectionHeader icon={<HeartIcon className="w-5 h-5" data-id="element-17313" />} title="Венера в Раке" subtitle="Любовь, ценности и эстетика" color="rose" data-id="element-17312" />
          <div className="space-y-6" data-id="element-17314">
            <div data-id="element-17315">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-17316">Язык любви</h3>
              {renderParagraphs(EXTENDED_VENUS.loveLanguage)}
            </div>
            <div data-id="element-17317">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-17318">Эстетика и вкус</h3>
              {renderParagraphs(EXTENDED_VENUS.aesthetics)}
            </div>
            <div data-id="element-17319">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-17320">
                Финансовые привычки
              </h3>
              {renderParagraphs(EXTENDED_VENUS.financialHabits)}
            </div>
          </div>
        </div>

        {/* 08. МАРС */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-amber-50/30 to-white" data-id="element-17321">
          <SectionHeader icon={<ZapIcon className="w-5 h-5" data-id="element-17323" />} title="Марс в Близнецах" subtitle="Энергия, действие и конфликт" color="amber" data-id="element-17322" />
          <div className="space-y-6" data-id="element-17324">
            <div data-id="element-17325">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-17326">Стиль конфликта</h3>
              {renderParagraphs(EXTENDED_MARS.conflictStyle)}
            </div>
            <div data-id="element-17327">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-17328">
                Амбиции и действия
              </h3>
              {renderParagraphs(EXTENDED_MARS.ambitionStyle)}
            </div>
            <div data-id="element-17329">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-17330">
                Физическая энергия
              </h3>
              {renderParagraphs(EXTENDED_MARS.physicalEnergy)}
            </div>
          </div>
        </div>

        {/* 09. ПЛАНЕТЫ В ДОМАХ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-17331">
          <SectionHeader icon={<LayersIcon className="w-5 h-5" data-id="element-17333" />} title="Планеты в домах" subtitle="Сферы жизни и где проявляется ваша энергия" color="indigo" data-id="element-17332" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-17334">
            {HOUSES_DETAILED.map((house, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: i * 0.05
          }} className={`rounded-xl p-5 border ${house.planets.length > 0 ? 'bg-indigo-50/50 border-indigo-200 shadow-sm' : 'bg-slate-50 border-slate-200'}`} data-id="element-17335">
                <div className="flex items-center justify-between mb-3" data-id="element-17336">
                  <div className="flex items-center gap-2" data-id="element-17337">
                    <span className={`w-6 h-6 rounded-md flex items-center justify-center text-xs font-bold ${house.planets.length > 0 ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-200 text-slate-600'}`} data-id="element-17338">
                      {house.num}
                    </span>
                    <h4 className="font-bold text-slate-900 text-sm" data-id="element-17339">
                      {house.theme}
                    </h4>
                  </div>
                  <span className="text-xs font-medium text-slate-500" data-id="element-17340">
                    {house.sign}
                  </span>
                </div>
                {house.planets.length > 0 && <div className="mb-2 flex flex-wrap gap-1" data-id="element-17341">
                    {house.planets.map((p, idx) => <Badge key={idx} variant="pro" className="text-[10px] px-1.5 py-0" data-id="element-17342">
                        {p}
                      </Badge>)}
                  </div>}
                <p className="text-sm text-slate-600 leading-relaxed" data-id="element-17343">
                  {house.text}
                </p>
              </motion.div>)}
          </div>
        </div>

        {/* 10. АСПЕКТЫ МЕЖДУ ПЛАНЕТАМИ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-indigo-50/30 to-white" data-id="element-17344">
          <SectionHeader icon={<ActivityIcon className="w-5 h-5" data-id="element-17346" />} title="Аспекты между планетами" subtitle="Внутренние диалоги и конфликты" color="indigo" data-id="element-17345" />
          <div className="space-y-4" data-id="element-17347">
            {ASPECTS_DETAILED.map((aspect, i) => {
            const isHarmonious = aspect.nature === 'harmonious';
            const isIntense = aspect.nature === 'intense';
            return <motion.div key={i} initial={{
              opacity: 0,
              x: -10
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: i * 0.08
            }} className={`rounded-xl p-5 border ${isHarmonious ? 'bg-emerald-50/50 border-emerald-200' : isIntense ? 'bg-purple-50/50 border-purple-200' : 'bg-rose-50/50 border-rose-200'}`} data-id="element-17348">
                  <div className="flex flex-wrap items-start sm:items-center justify-between gap-2 sm:gap-3 mb-3" data-id="element-17349">
                    <div className="flex items-center gap-2 sm:gap-3" data-id="element-17350">
                      <span className={`text-lg font-bold ${isHarmonious ? 'text-emerald-600' : isIntense ? 'text-purple-600' : 'text-rose-600'}`} data-id="element-17351">
                        {aspect.symbol}
                      </span>
                      <h4 className="font-bold text-slate-900 text-sm sm:text-base" data-id="element-17352">
                        {aspect.title}
                      </h4>
                    </div>
                    <Badge variant={isHarmonious ? 'success' : isIntense ? 'pro' : 'warning'} className="text-xs" data-id="element-17353">
                      {isHarmonious ? 'Гармоничный' : isIntense ? 'Интенсивный' : 'Напряжённый'}
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed" data-id="element-17354">
                    {aspect.text}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>

        {/* 11. КАРЬЕРА И ФИНАНСЫ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-17355">
          <SectionHeader icon={<BriefcaseIcon className="w-5 h-5" data-id="element-17357" />} title="Карьера и финансы" subtitle="Профессиональный потенциал и заработок" color="emerald" data-id="element-17356" />
          <div className="space-y-6" data-id="element-17358">
            <div data-id="element-17359">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-17360">
                Профессиональный путь
              </h3>
              {renderParagraphs(EXTENDED_JUPITER)}
            </div>
            <div data-id="element-17361">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-17362">
                Финансовая стратегия
              </h3>
              {renderParagraphs(OUTER_PLANETS_EXTENDED.uranus)}
            </div>
            <div className="bg-emerald-50 rounded-xl p-5 border border-emerald-200" data-id="element-17363">
              <h3 className="font-bold text-emerald-800 mb-3" data-id="element-17364">
                Лучшие сферы для реализации
              </h3>
              <ul className="space-y-2" data-id="element-17365">
                {TALENTS_SECTION.bestAreas.split('\n').filter(line => line.trim().startsWith('•')).map((area, i) => <li key={i} className="flex items-start gap-2 text-sm text-emerald-700" data-id="element-17366">
                      <CheckIcon className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-17367" />
                      {area.replace('•', '').trim()}
                    </li>)}
              </ul>
            </div>
          </div>
        </div>

        {/* 12. ЛЮБОВЬ И ОТНОШЕНИЯ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-rose-50/30 to-white" data-id="element-17368">
          <SectionHeader icon={<HeartIcon className="w-5 h-5" data-id="element-17370" />} title="Любовь и отношения" subtitle="Совместимость и партнёрство" color="rose" data-id="element-17369" />
          <div className="space-y-6 mb-8" data-id="element-17371">
            <p className="text-slate-600 leading-relaxed" data-id="element-17372">
              Ваш подход к отношениям формируется Венерой в Раке (потребность в
              заботе и уюте), Марсом в Близнецах (потребность в интеллектуальном
              стимулировании) и 7 домом в Близнецах. Вы ищете партнёра, с
              которым можно и глубоко помолчать, и жарко поспорить.
            </p>
          </div>

          <h3 className="font-bold text-slate-800 mb-4" data-id="element-17373">
            Совместимость со знаками зодиака
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-id="element-17374">
            {ZODIAC_COMPATIBILITY.map((sign, i) => <div key={i} className={`p-3 rounded-xl border ${sign.best ? 'bg-rose-50 border-rose-200 shadow-sm' : 'bg-white border-slate-200'}`} data-id="element-17375">
                <div className="flex items-center justify-between mb-2" data-id="element-17376">
                  <div className="flex items-center gap-1.5" data-id="element-17377">
                    <span className={`text-lg ${sign.best ? 'text-rose-500' : 'text-slate-400'}`} data-id="element-17378">
                      {sign.symbol}
                    </span>
                    <span className="font-bold text-slate-800 text-sm" data-id="element-17379">
                      {sign.sign}
                    </span>
                  </div>
                  <span className={`text-xs font-bold ${sign.percent >= 80 ? 'text-emerald-600' : sign.percent >= 70 ? 'text-amber-600' : 'text-slate-500'}`} data-id="element-17380">
                    {sign.percent}%
                  </span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-1.5 mb-2 overflow-hidden" data-id="element-17381">
                  <div className={`h-1.5 rounded-full ${sign.percent >= 80 ? 'bg-emerald-500' : sign.percent >= 70 ? 'bg-amber-500' : 'bg-slate-400'}`} style={{
                width: `${sign.percent}%`
              }} data-id="element-17382" />
                </div>
                <p className="text-[10px] text-slate-500 leading-tight" data-id="element-17383">
                  {sign.note}
                </p>
              </div>)}
          </div>
        </div>

        {/* 13. КАРМИЧЕСКИЕ ЗАДАЧИ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-17384">
          <SectionHeader icon={<CompassIcon className="w-5 h-5" data-id="element-17386" />} title="Кармические задачи" subtitle="Предназначение и уроки души" color="purple" data-id="element-17385" />
          <div className="space-y-6" data-id="element-17387">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-17388">
              <div className="bg-purple-50 rounded-xl p-5 border border-purple-200" data-id="element-17389">
                <h3 className="font-bold text-purple-800 mb-2 flex items-center gap-2" data-id="element-17390">
                  <TrendingUpIcon className="w-4 h-4" data-id="element-17391" /> Вектор развития
                  (Северный узел)
                </h3>
                {renderParagraphs(KARMIC_SECTION.northNode)}
              </div>
              <div className="bg-slate-50 rounded-xl p-5 border border-slate-200" data-id="element-17392">
                <h3 className="font-bold text-slate-700 mb-2 flex items-center gap-2" data-id="element-17393">
                  <AlertTriangleIcon className="w-4 h-4" data-id="element-17394" /> Прошлый опыт (Южный
                  узел)
                </h3>
                {renderParagraphs(KARMIC_SECTION.southNode)}
              </div>
            </div>

            <div data-id="element-17395">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-17396">
                Главный урок Сатурна
              </h3>
              {renderParagraphs(KARMIC_SECTION.saturnLesson)}
            </div>

            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-6 text-white shadow-lg" data-id="element-17397">
              <h3 className="font-bold text-white mb-2 text-lg" data-id="element-17398">
                Жизненная миссия
              </h3>
              <p className="text-indigo-100 leading-relaxed" data-id="element-17399">
                {KARMIC_SECTION.lifeMission}
              </p>
            </div>

            <div className="mt-8" data-id="element-17400">
              <h3 className="font-bold text-slate-800 mb-4" data-id="element-17401">Главные вызовы</h3>
              <div className="space-y-3" data-id="element-17402">
                {CHALLENGES_SECTION.mainChallenges.split('\n\n').filter(p => !p.startsWith('Каждая')).map((challenge, i) => <div key={i} className="flex items-start gap-3 bg-white p-4 rounded-xl border border-rose-100" data-id="element-17403">
                      <div className="w-6 h-6 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5" data-id="element-17404">
                        {i + 1}
                      </div>
                      <p className="text-sm text-slate-700 leading-relaxed" data-id="element-17405">
                        {challenge}
                      </p>
                    </div>)}
              </div>
            </div>
          </div>
        </div>

        {/* 14. ТАЛАНТЫ И ПРЕДНАЗНАЧЕНИЕ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-amber-50/30 to-white" data-id="element-17406">
          <SectionHeader icon={<TargetIcon className="w-5 h-5" data-id="element-17408" />} title="Таланты и здоровье" subtitle="Ваши сильные стороны и физический потенциал" color="amber" data-id="element-17407" />

          <div className="space-y-8" data-id="element-17409">
            <div data-id="element-17410">
              <h3 className="font-bold text-slate-800 mb-4" data-id="element-17411">
                Природные способности
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-17412">
                {TALENTS_SECTION.naturalAbilities.split('\n\n').filter(p => !p.startsWith('На основе')).map((talent, i) => <div key={i} className="bg-white rounded-xl p-4 border border-amber-200 shadow-sm" data-id="element-17413">
                      <p className="text-sm text-slate-700 leading-relaxed" data-id="element-17414">
                        {talent}
                      </p>
                    </div>)}
              </div>
            </div>

            <div data-id="element-17415">
              <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2" data-id="element-17416">
                <ActivityIcon className="w-5 h-5 text-emerald-500" data-id="element-17417" />{' '}
                Астрологическое здоровье
              </h3>
              <p className="text-slate-600 leading-relaxed mb-4" data-id="element-17418">
                {HEALTH_SECTION.overview}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-17419">
                <Card padding="md" className="bg-emerald-50/50 border-emerald-100" data-id="element-17420">
                  <h4 className="font-bold text-emerald-800 mb-2 text-sm" data-id="element-17421">
                    Витальность (Солнце)
                  </h4>
                  <p className="text-xs text-slate-600" data-id="element-17422">
                    {HEALTH_SECTION.sunHealth}
                  </p>
                </Card>
                <Card padding="md" className="bg-indigo-50/50 border-indigo-100" data-id="element-17423">
                  <h4 className="font-bold text-indigo-800 mb-2 text-sm" data-id="element-17424">
                    Психосоматика (Луна)
                  </h4>
                  <p className="text-xs text-slate-600" data-id="element-17425">
                    {HEALTH_SECTION.moonHealth}
                  </p>
                </Card>
              </div>
              <div className="bg-white rounded-xl p-4 border border-slate-200" data-id="element-17426">
                <h4 className="font-bold text-slate-800 mb-3 text-sm" data-id="element-17427">
                  Рекомендации по здоровью
                </h4>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-id="element-17428">
                  {HEALTH_SECTION.recommendations.split('\n').map((rec, i) => <li key={i} className="flex items-start gap-2 text-xs text-slate-600" data-id="element-17429">
                      <CheckIcon className="w-3.5 h-3.5 mt-0.5 text-emerald-500 flex-shrink-0" data-id="element-17430" />
                      {rec.replace('•', '').trim()}
                    </li>)}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* 15. ИТОГОВЫЙ АНАЛИЗ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-17431">
          <SectionHeader icon={<SparklesIcon className="w-5 h-5" data-id="element-17433" />} title="Итоговый анализ" subtitle="Синтез натальной карты" color="indigo" data-id="element-17432" />

          <div className="space-y-6 mb-8" data-id="element-17434">
            {renderParagraphs(FINAL_ANALYSIS.summary)}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" data-id="element-17435">
            <div className="bg-indigo-50 rounded-xl p-5 border border-indigo-200" data-id="element-17436">
              <h3 className="font-bold text-indigo-800 mb-3" data-id="element-17437">
                Ключевые качества
              </h3>
              <ul className="space-y-2" data-id="element-17438">
                {FINAL_ANALYSIS.keyQualities.map((q, i) => <li key={i} className="flex items-start gap-2 text-sm text-indigo-900" data-id="element-17439">
                    <StarIcon className="w-4 h-4 mt-0.5 text-indigo-500 flex-shrink-0" data-id="element-17440" />
                    {q}
                  </li>)}
              </ul>
            </div>
            <div className="bg-amber-50 rounded-xl p-5 border border-amber-200" data-id="element-17441">
              <h3 className="font-bold text-amber-800 mb-3" data-id="element-17442">
                Главные темы жизни
              </h3>
              <ul className="space-y-2" data-id="element-17443">
                {FINAL_ANALYSIS.lifeThemes.map((t, i) => <li key={i} className="flex items-start gap-2 text-sm text-amber-900" data-id="element-17444">
                    <CompassIcon className="w-4 h-4 mt-0.5 text-amber-500 flex-shrink-0" data-id="element-17445" />
                    {t}
                  </li>)}
              </ul>
            </div>
          </div>

          <h3 className="font-bold text-slate-800 mb-4" data-id="element-17446">
            Ваши персональные аффирмации
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-8" data-id="element-17447">
            {PERSONAL_AFFIRMATIONS.map((aff, i) => <div key={i} className={`p-4 rounded-xl border border-slate-200 bg-gradient-to-br ${aff.gradient}`} data-id="element-17448">
                <p className="font-medium text-slate-800 text-sm mb-2" data-id="element-17449">
                  «{aff.text}»
                </p>
                <p className="text-[10px] text-slate-500 uppercase tracking-wider" data-id="element-17450">
                  {aff.source}
                </p>
              </div>)}
          </div>

          <h3 className="font-bold text-slate-800 mb-4" data-id="element-17451">
            Знаменитости с похожими показателями
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8" data-id="element-17452">
            {CELEBRITY_MATCHES.map((celeb, i) => <div key={i} className="flex items-center gap-4 p-3 rounded-xl border border-slate-100 bg-slate-50" data-id="element-17453">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0 ${celeb.color}`} data-id="element-17454">
                  {celeb.initials}
                </div>
                <div data-id="element-17455">
                  <div className="font-bold text-slate-900 text-sm" data-id="element-17456">
                    {celeb.name}
                  </div>
                  <div className="text-xs text-indigo-600 font-medium mb-0.5" data-id="element-17457">
                    {celeb.sharedPlacement}
                  </div>
                  <div className="text-[10px] text-slate-500" data-id="element-17458">{celeb.note}</div>
                </div>
              </div>)}
          </div>

          <div className="bg-gradient-to-br from-amber-100 via-yellow-50 to-amber-100 rounded-2xl p-6 sm:p-8 text-center shadow-sm border border-amber-200/60" data-id="element-17459">
            <SparklesIcon className="w-8 h-8 text-amber-500 mx-auto mb-4" data-id="element-17460" />
            <p className="text-base sm:text-lg font-semibold leading-relaxed text-slate-800 italic max-w-2xl mx-auto" data-id="element-17461">
              {FINAL_ANALYSIS.finalAdvice}
            </p>
          </div>
        </div>
      </>;
  };
  // ═══════════════════════════════════════════
  // RENDER SOLAR CONTENT
  // ═══════════════════════════════════════════
  const renderSolarContent = () => {
    const planetIcons: Record<string, React.ReactNode> = {
      Меркурий: <MessageCircleIcon className="w-5 h-5" data-id="element-17462" />,
      Венера: <HeartIcon className="w-5 h-5" data-id="element-17463" />,
      Марс: <ZapIcon className="w-5 h-5" data-id="element-17464" />,
      Юпитер: <TrendingUpIcon className="w-5 h-5" data-id="element-17465" />,
      Сатурн: <ShieldIcon className="w-5 h-5" data-id="element-17466" />,
      Уран: <SparklesIcon className="w-5 h-5" data-id="element-17467" />,
      Нептун: <CompassIcon className="w-5 h-5" data-id="element-17468" />,
      Плутон: <ActivityIcon className="w-5 h-5" data-id="element-17469" />
    };
    const areaIcons: Record<string, React.ReactNode> = {
      briefcase: <BriefcaseIcon className="w-5 h-5" data-id="element-17470" />,
      heart: <HeartIcon className="w-5 h-5" data-id="element-17471" />,
      home: <StarIcon className="w-5 h-5" data-id="element-17472" />,
      'trending-up': <TrendingUpIcon className="w-5 h-5" data-id="element-17473" />,
      activity: <ActivityIcon className="w-5 h-5" data-id="element-17474" />,
      users: <UsersIcon className="w-5 h-5" data-id="element-17475" />
    };
    const areaColorMap: Record<string, {
      bg: string;
      border: string;
      text: string;
      iconBg: string;
    }> = {
      emerald: {
        bg: 'bg-emerald-50',
        border: 'border-emerald-200',
        text: 'text-emerald-700',
        iconBg: 'bg-emerald-100 text-emerald-600'
      },
      rose: {
        bg: 'bg-rose-50',
        border: 'border-rose-200',
        text: 'text-rose-700',
        iconBg: 'bg-rose-100 text-rose-600'
      },
      amber: {
        bg: 'bg-amber-50',
        border: 'border-amber-200',
        text: 'text-amber-700',
        iconBg: 'bg-amber-100 text-amber-600'
      },
      indigo: {
        bg: 'bg-indigo-50',
        border: 'border-indigo-200',
        text: 'text-indigo-700',
        iconBg: 'bg-indigo-100 text-indigo-600'
      },
      teal: {
        bg: 'bg-teal-50',
        border: 'border-teal-200',
        text: 'text-teal-700',
        iconBg: 'bg-teal-100 text-teal-600'
      },
      sky: {
        bg: 'bg-sky-50',
        border: 'border-sky-200',
        text: 'text-sky-700',
        iconBg: 'bg-sky-100 text-sky-600'
      }
    };
    // Helper: render a section header
    const SectionHeader = ({
      icon,
      title,
      subtitle
    }: {
      icon: React.ReactNode;
      title: string;
      subtitle?: string;
    }) => <div className="flex items-center gap-3 mb-4 sm:mb-6" data-id="element-17476">
        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center" data-id="element-17477">
          {icon}
        </div>
        <div className="min-w-0" data-id="element-17478">
          <h2 className="text-lg sm:text-xl font-bold text-slate-900 leading-tight" data-id="element-17479">
            {title}
          </h2>
          {subtitle && <p className="text-xs sm:text-sm text-slate-500" data-id="element-17480">{subtitle}</p>}
        </div>
      </div>;
    // Helper: render strengths/cautions cards
    const StrengthsCautions = ({
      strengths,
      cautions
    }: {
      strengths: string[];
      cautions: string[];
    }) => <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6" data-id="element-17481">
        <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100" data-id="element-17482">
          <h4 className="font-bold text-emerald-800 mb-3 flex items-center gap-2" data-id="element-17483">
            <ShieldIcon className="w-4 h-4" data-id="element-17484" /> Сильные стороны
          </h4>
          <ul className="space-y-1.5 text-sm text-emerald-700" data-id="element-17485">
            {strengths.map((s, i) => <li key={i} className="flex items-start gap-2" data-id="element-17486">
                <CheckIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" data-id="element-17487" />
                {s}
              </li>)}
          </ul>
        </div>
        <div className="bg-amber-50 rounded-xl p-4 border border-amber-100" data-id="element-17488">
          <h4 className="font-bold text-amber-800 mb-3 flex items-center gap-2" data-id="element-17489">
            <AlertTriangleIcon className="w-4 h-4" data-id="element-17490" /> Зоны внимания
          </h4>
          <ul className="space-y-1.5 text-sm text-amber-700" data-id="element-17491">
            {cautions.map((c, i) => <li key={i} className="flex items-start gap-2" data-id="element-17492">
                <AlertTriangleIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" data-id="element-17493" />
                {c}
              </li>)}
          </ul>
        </div>
      </div>;
    // Individual planet sections (Mercury, Venus, Mars shown separately)
    const individualPlanets = SOLAR_PLANETS_IN_HOUSES.slice(0, 3);
    // Grouped planets (Jupiter, Saturn, outer)
    const groupedPlanets = SOLAR_PLANETS_IN_HOUSES.slice(3);
    return <>
        {/* ══════════ 01. ВВЕДЕНИЕ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-17494">
          <SectionHeader icon={<BookOpenIcon className="w-5 h-5" data-id="element-17496" />} title={SOLAR_INTRO.title} subtitle="Что такое соляр и как работает прогноз" data-id="element-17495" />
          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-17497">
            <p data-id="element-17498">{SOLAR_INTRO.whatIsSolar}</p>
            <p data-id="element-17499">{SOLAR_INTRO.howItAffects}</p>
          </div>
        </div>

        {/* ══════════ 02. ОБЩАЯ ТЕМА ГОДА ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-amber-50/50 to-white" data-id="element-17500">
          <SectionHeader icon={<SunIcon className="w-5 h-5" data-id="element-17502" />} title="Общая тема года" subtitle="Ключевые энергии 2026 года" data-id="element-17501" />

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 mb-6" data-id="element-17503">
            <Card padding="md" className="bg-white border-amber-200" data-id="element-17504">
              <div className="flex items-center gap-2 mb-3" data-id="element-17505">
                <TargetIcon className="w-5 h-5 text-amber-500" data-id="element-17506" />
                <span className="text-xs font-bold text-amber-600 uppercase tracking-wider" data-id="element-17507">
                  Тема года
                </span>
              </div>
              <div className="text-lg font-bold text-slate-900" data-id="element-17508">
                {SOLAR_YEAR_THEME.mainEnergy}
              </div>
              <p className="text-xs text-slate-500 mt-2" data-id="element-17509">
                Солнце и Сатурн в 10 доме
              </p>
            </Card>
            <Card padding="md" className="bg-white border-orange-200" data-id="element-17510">
              <div className="flex items-center gap-2 mb-3" data-id="element-17511">
                <BriefcaseIcon className="w-5 h-5 text-orange-500" data-id="element-17512" />
                <span className="text-xs font-bold text-orange-600 uppercase tracking-wider" data-id="element-17513">
                  Ключевая сфера
                </span>
              </div>
              <div className="text-lg font-bold text-slate-900" data-id="element-17514">
                Карьера и статус
              </div>
              <p className="text-xs text-slate-500 mt-2" data-id="element-17515">10 дом активирован</p>
            </Card>
            <Card padding="md" className="bg-white border-emerald-200" data-id="element-17516">
              <div className="flex items-center gap-2 mb-3" data-id="element-17517">
                <TrendingUpIcon className="w-5 h-5 text-emerald-500" data-id="element-17518" />
                <span className="text-xs font-bold text-emerald-600 uppercase tracking-wider" data-id="element-17519">
                  Лучший период
                </span>
              </div>
              <div className="text-lg font-bold text-slate-900" data-id="element-17520">Март — Май</div>
              <p className="text-xs text-slate-500 mt-2" data-id="element-17521">
                Максимум возможностей
              </p>
            </Card>
          </div>

          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-17522">
            <p data-id="element-17523">{SOLAR_YEAR_THEME.description}</p>
            <p data-id="element-17524">
              <strong className="text-slate-800" data-id="element-17525">Основные изменения:</strong>{' '}
              {SOLAR_YEAR_THEME.changes}
            </p>
            <p data-id="element-17526">
              <strong className="text-slate-800" data-id="element-17527">
                Психологическая атмосфера:
              </strong>{' '}
              {SOLAR_YEAR_THEME.atmosphere}
            </p>
            <p data-id="element-17528">
              <strong className="text-slate-800" data-id="element-17529">Точки роста:</strong>{' '}
              {SOLAR_YEAR_THEME.growthPoints}
            </p>
          </div>

          <div className="mt-6 bg-amber-100/50 rounded-xl p-4 border border-amber-200" data-id="element-17530">
            <h4 className="font-bold text-amber-800 mb-3" data-id="element-17531">
              Главные задачи года
            </h4>
            <ul className="space-y-1.5 text-sm text-amber-700" data-id="element-17532">
              {SOLAR_YEAR_THEME.tasks.map((task, i) => <li key={i} className="flex items-start gap-2" data-id="element-17533">
                  <TargetIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" data-id="element-17534" />
                  {task}
                </li>)}
            </ul>
          </div>
        </div>

        {/* ══════════ 03. АСЦЕНДЕНТ СОЛЯРА ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-17535">
          <SectionHeader icon={<StarIcon className="w-5 h-5" data-id="element-17537" />} title={SOLAR_ASCENDANT.title} subtitle={`Восходящий знак: ${SOLAR_ASCENDANT.symbol} ${SOLAR_ASCENDANT.sign}`} data-id="element-17536" />

          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-17538">
            <p data-id="element-17539">{SOLAR_ASCENDANT.description}</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 my-6" data-id="element-17540">
              <Card padding="md" className="bg-white border-indigo-200" data-id="element-17541">
                <h4 className="text-xs font-bold text-indigo-600 uppercase tracking-wider mb-2" data-id="element-17542">
                  Проявление
                </h4>
                <p className="text-sm text-slate-600" data-id="element-17543">
                  {SOLAR_ASCENDANT.manifestation}
                </p>
              </Card>
              <Card padding="md" className="bg-white border-amber-200" data-id="element-17544">
                <h4 className="text-xs font-bold text-amber-600 uppercase tracking-wider mb-2" data-id="element-17545">
                  Стратегия
                </h4>
                <p className="text-sm text-slate-600" data-id="element-17546">
                  {SOLAR_ASCENDANT.strategy}
                </p>
              </Card>
              <Card padding="md" className="bg-white border-emerald-200" data-id="element-17547">
                <h4 className="text-xs font-bold text-emerald-600 uppercase tracking-wider mb-2" data-id="element-17548">
                  Восприятие
                </h4>
                <p className="text-sm text-slate-600" data-id="element-17549">
                  {SOLAR_ASCENDANT.perception}
                </p>
              </Card>
            </div>
          </div>
        </div>

        {/* ══════════ 04. СОЛНЦЕ В СОЛЯРЕ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-amber-50/30 to-white" data-id="element-17550">
          <SectionHeader icon={<SunIcon className="w-5 h-5" data-id="element-17552" />} title={SOLAR_SUN_POSITION.title} subtitle={`${SOLAR_SUN_POSITION.sign} • ${SOLAR_SUN_POSITION.house}-й дом`} data-id="element-17551" />

          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-17553">
            <p data-id="element-17554">{SOLAR_SUN_POSITION.description}</p>
            <p data-id="element-17555">
              <strong className="text-slate-800" data-id="element-17556">Ключевая сфера:</strong>{' '}
              {SOLAR_SUN_POSITION.keySphere}
            </p>
            <p data-id="element-17557">{SOLAR_SUN_POSITION.focus}</p>
          </div>

          <div className="mt-6 bg-white rounded-xl p-4 border border-amber-200" data-id="element-17558">
            <h4 className="font-bold text-amber-800 mb-3" data-id="element-17559">Возможные события</h4>
            <ul className="space-y-1.5 text-sm text-slate-700" data-id="element-17560">
              {SOLAR_SUN_POSITION.events.map((event, i) => <li key={i} className="flex items-start gap-2" data-id="element-17561">
                  <SunIcon className="w-3.5 h-3.5 mt-0.5 text-amber-500 flex-shrink-0" data-id="element-17562" />
                  {event}
                </li>)}
            </ul>
          </div>

          <StrengthsCautions strengths={SOLAR_SUN_POSITION.strengths} cautions={SOLAR_SUN_POSITION.cautions} data-id="element-17563" />
        </div>

        {/* ══════════ 05. ЛУНА В СОЛЯРЕ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-17564">
          <SectionHeader icon={<MoonIcon className="w-5 h-5" data-id="element-17566" />} title={SOLAR_MOON.title} subtitle={`${SOLAR_MOON.sign} • ${SOLAR_MOON.house}-й дом`} data-id="element-17565" />

          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-17567">
            <p data-id="element-17568">{SOLAR_MOON.description}</p>
            <p data-id="element-17569">
              <strong className="text-slate-800" data-id="element-17570">Эмоциональный фон:</strong>{' '}
              {SOLAR_MOON.emotions}
            </p>
            <p data-id="element-17571">
              <strong className="text-slate-800" data-id="element-17572">
                Внутренние потребности:
              </strong>{' '}
              {SOLAR_MOON.needs}
            </p>
            <p data-id="element-17573">
              <strong className="text-slate-800" data-id="element-17574">Отношения с близкими:</strong>{' '}
              {SOLAR_MOON.relations}
            </p>
          </div>

          <StrengthsCautions strengths={SOLAR_MOON.strengths} cautions={SOLAR_MOON.cautions} data-id="element-17575" />
        </div>

        {/* ══════════ 06-08. INDIVIDUAL PLANETS (Mercury, Venus, Mars) ══════════ */}
        {individualPlanets.map((planet, idx) => <div key={idx} className={`p-4 sm:p-6 md:p-8 border-b border-slate-100 ${idx % 2 === 0 ? 'bg-gradient-to-br from-slate-50/50 to-white' : ''}`} data-id="element-17576">
            <SectionHeader icon={planetIcons[planet.planet] || <StarIcon className="w-5 h-5" data-id="element-17578" />} title={planet.title} subtitle={`${planet.symbol} ${planet.sign} • ${planet.house}-й дом`} data-id="element-17577" />
            <p className="text-slate-600 leading-relaxed mb-4" data-id="element-17579">
              {planet.description}
            </p>
            <div className="bg-white rounded-xl p-4 border border-slate-200" data-id="element-17580">
              <h4 className="font-bold text-slate-800 mb-3" data-id="element-17581">
                Возможные события
              </h4>
              <ul className="space-y-1.5 text-sm text-slate-700" data-id="element-17582">
                {planet.events.map((event, i) => <li key={i} className="flex items-start gap-2" data-id="element-17583">
                    <CheckIcon className="w-3.5 h-3.5 mt-0.5 text-amber-500 flex-shrink-0" data-id="element-17584" />
                    {event}
                  </li>)}
              </ul>
            </div>
          </div>)}

        {/* ══════════ 09. ЮПИТЕР, САТУРН И ВНЕШНИЕ ПЛАНЕТЫ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-indigo-50/30 to-white" data-id="element-17585">
          <SectionHeader icon={<LayersIcon className="w-5 h-5" data-id="element-17587" />} title="Юпитер, Сатурн и внешние планеты" subtitle="Долгосрочные тенденции года" data-id="element-17586" />

          <div className="space-y-6" data-id="element-17588">
            {groupedPlanets.map((planet, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: idx * 0.1
          }} className="bg-white rounded-xl p-5 border border-slate-200" data-id="element-17589">
                <div className="flex items-center gap-3 mb-3" data-id="element-17590">
                  <div className="w-8 h-8 rounded-lg bg-indigo-100 text-indigo-600 flex items-center justify-center text-lg" data-id="element-17591">
                    {planet.symbol}
                  </div>
                  <div data-id="element-17592">
                    <h4 className="font-bold text-slate-900" data-id="element-17593">{planet.title}</h4>
                    <p className="text-xs text-slate-500" data-id="element-17594">
                      {planet.sign} • {planet.house}-й дом
                    </p>
                  </div>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed mb-3" data-id="element-17595">
                  {planet.description}
                </p>
                <div className="flex flex-wrap gap-2" data-id="element-17596">
                  {planet.events.map((event, i) => <span key={i} className="text-xs bg-indigo-50 text-indigo-700 px-2.5 py-1 rounded-full border border-indigo-100" data-id="element-17597">
                      {event}
                    </span>)}
                </div>
              </motion.div>)}
          </div>
        </div>

        {/* ══════════ 10. АСПЕКТЫ СОЛЯРА ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-17598">
          <SectionHeader icon={<SparklesIcon className="w-5 h-5" data-id="element-17600" />} title="Аспекты соляра" subtitle="Взаимодействие планетарных энергий года" color="amber" data-id="element-17599" />
          <div className="space-y-4" data-id="element-17601">
            {COMBO_SOLAR_ASPECTS.map((aspect, i) => {
            const isHarmonious = aspect.nature === 'harmonious';
            return <motion.div key={i} initial={{
              opacity: 0,
              x: -10
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: i * 0.1
            }} className={`rounded-xl p-5 border ${isHarmonious ? 'bg-emerald-50/50 border-emerald-200' : 'bg-rose-50/50 border-rose-200'}`} data-id="element-17602">
                  <div className="flex flex-wrap items-start sm:items-center justify-between gap-2 sm:gap-3 mb-3" data-id="element-17603">
                    <div className="flex items-center gap-2 sm:gap-3" data-id="element-17604">
                      <span className={`text-lg font-bold ${isHarmonious ? 'text-emerald-600' : 'text-rose-600'}`} data-id="element-17605">
                        {aspect.aspect}
                      </span>
                      <h4 className="font-bold text-slate-900 text-sm sm:text-base" data-id="element-17606">
                        {aspect.title}
                      </h4>
                    </div>
                    <Badge variant={isHarmonious ? 'success' : 'warning'} className="text-xs" data-id="element-17607">
                      {isHarmonious ? 'Гармоничный' : 'Напряжённый'}
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed" data-id="element-17608">
                    {aspect.text}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>

        {/* ══════════ 11-13. СФЕРЫ ЖИЗНИ ══════════ */}
        {SOLAR_LIFE_AREAS.map((area, idx) => {
        const colors = areaColorMap[area.color] || areaColorMap.emerald;
        // Group: 11 = career+relationships, 12 = family, 13 = growth+health+social
        // Show section headers for grouping
        const showCareerHeader = idx === 0;
        const showRelationsHeader = idx === 1;
        const showGrowthHeader = idx === 3;
        return <Fragment key={area.id} data-id="element-17609">
              {showCareerHeader && <div className="p-4 sm:p-6 md:p-8 pb-0 border-b-0" data-id="element-17610">
                  <SectionHeader icon={<BriefcaseIcon className="w-5 h-5" data-id="element-17612" />} title="Ключевые сферы жизни года" subtitle="Подробный разбор каждой области" data-id="element-17611" />
                </div>}
              <div className={`p-4 sm:p-6 md:p-8 pb-6 ${idx === SOLAR_LIFE_AREAS.length - 1 ? 'border-b border-slate-100' : ''}`} data-id="element-17613">
                <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: idx * 0.08
            }} className={`${colors.bg} rounded-xl p-5 border ${colors.border}`} data-id="element-17614">
                  <div className="flex items-center gap-3 mb-3" data-id="element-17615">
                    <div className={`w-8 h-8 rounded-lg ${colors.iconBg} flex items-center justify-center`} data-id="element-17616">
                      {areaIcons[area.icon] || <StarIcon className="w-4 h-4" data-id="element-17617" />}
                    </div>
                    <h4 className="font-bold text-slate-900" data-id="element-17618">{area.title}</h4>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed" data-id="element-17619">
                    {area.analysis}
                  </p>
                </motion.div>
              </div>
            </Fragment>;
      })}

        {/* ══════════ 14. ВАЖНЫЕ ПЕРИОДЫ ГОДА ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-17620">
          <SectionHeader icon={<CalendarIcon className="w-5 h-5" data-id="element-17622" />} title="Важные периоды года" subtitle="Помесячный прогноз активности" data-id="element-17621" />

          {/* Monthly Forecast Chart */}
          <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-12 gap-1.5 sm:gap-2 mb-6" data-id="element-17623">
            {SOLAR_MONTHLY_FORECAST.map((month, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: i * 0.05
          }} className="text-center" data-id="element-17624">
                <div className="text-[10px] text-slate-400 mb-1" data-id="element-17625">
                  {month.month}
                </div>
                <div className="relative h-20 bg-slate-100 rounded-full overflow-hidden" data-id="element-17626">
                  <motion.div className={`absolute bottom-0 w-full rounded-full ${month.intensity >= 80 ? 'bg-amber-500' : month.intensity >= 60 ? 'bg-amber-400' : 'bg-amber-300'}`} initial={{
                height: 0
              }} animate={{
                height: `${month.intensity}%`
              }} transition={{
                duration: 0.8,
                delay: i * 0.05
              }} data-id="element-17627" />
                </div>
                <div className="text-[9px] text-slate-400 mt-1 truncate" data-id="element-17628">
                  {month.theme}
                </div>
              </motion.div>)}
          </div>
          <div className="flex justify-center gap-4 mb-8 text-xs text-slate-500" data-id="element-17629">
            <span className="flex items-center gap-1" data-id="element-17630">
              <span className="w-3 h-3 rounded-full bg-amber-500" data-id="element-17631"></span>{' '}
              Высокая активность
            </span>
            <span className="flex items-center gap-1" data-id="element-17632">
              <span className="w-3 h-3 rounded-full bg-amber-300" data-id="element-17633"></span>{' '}
              Умеренная
            </span>
          </div>

          {/* Key Dates Grid */}
          <h3 className="text-lg font-bold text-slate-900 mb-4" data-id="element-17634">
            Ключевые даты года
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3" data-id="element-17635">
            {SOLAR_KEY_DATES.map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: i * 0.1
          }} className={`rounded-xl p-3 border ${item.type === 'positive' ? 'bg-emerald-50 border-emerald-200' : item.type === 'caution' ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200'}`} data-id="element-17636">
                <div className={`text-sm font-bold ${item.type === 'positive' ? 'text-emerald-700' : item.type === 'caution' ? 'text-amber-700' : 'text-slate-700'}`} data-id="element-17637">
                  {item.date}
                </div>
                <div className="text-xs text-slate-500 mt-1" data-id="element-17638">{item.event}</div>
              </motion.div>)}
          </div>

          {/* Sample Month Deep Dive */}
          <div className="mt-8" data-id="element-17639">
            <div className="flex items-center gap-3 mb-4" data-id="element-17640">
              <div className="w-8 h-8 rounded-lg bg-amber-100 text-amber-600 flex items-center justify-center" data-id="element-17641">
                <CalendarIcon className="w-4 h-4" data-id="element-17642" />
              </div>
              <div data-id="element-17643">
                <h3 className="font-bold text-slate-900" data-id="element-17644">Март 2026 — Прорыв</h3>
                <p className="text-xs text-slate-500" data-id="element-17645">
                  Самый активный месяц года
                </p>
              </div>
            </div>
            <p className="text-slate-600 leading-relaxed mb-4" data-id="element-17646">
              Март станет поворотным месяцем. Новолуние в вашем 10 доме 15 марта
              запускает новый карьерный цикл. Это идеальное время для запуска
              проектов, переговоров о повышении или смены работы. Юпитер в
              тригоне к натальному Солнцу усиливает вашу уверенность и
              притягивает удачу.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-17647">
              <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100" data-id="element-17648">
                <h4 className="font-bold text-emerald-800 mb-3" data-id="element-17649">
                  Благоприятно
                </h4>
                <ul className="space-y-1.5 text-sm text-emerald-700" data-id="element-17650">
                  <li className="flex items-start gap-2" data-id="element-17651">
                    <CheckIcon className="w-3.5 h-3.5 mt-0.5" data-id="element-17652" />
                    Карьерные переговоры
                  </li>
                  <li className="flex items-start gap-2" data-id="element-17653">
                    <CheckIcon className="w-3.5 h-3.5 mt-0.5" data-id="element-17654" />
                    Запуск проектов
                  </li>
                  <li className="flex items-start gap-2" data-id="element-17655">
                    <CheckIcon className="w-3.5 h-3.5 mt-0.5" data-id="element-17656" />
                    Публичные выступления
                  </li>
                </ul>
              </div>
              <div className="bg-amber-50 rounded-xl p-4 border border-amber-100" data-id="element-17657">
                <h4 className="font-bold text-amber-800 mb-3" data-id="element-17658">Осторожно</h4>
                <ul className="space-y-1.5 text-sm text-amber-700" data-id="element-17659">
                  <li className="flex items-start gap-2" data-id="element-17660">
                    <AlertTriangleIcon className="w-3.5 h-3.5 mt-0.5" data-id="element-17661" />
                    Импульсивные решения
                  </li>
                  <li className="flex items-start gap-2" data-id="element-17662">
                    <AlertTriangleIcon className="w-3.5 h-3.5 mt-0.5" data-id="element-17663" />
                    Переоценка сил
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* ══════════ 15. РЕКОМЕНДАЦИИ НА ГОД ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-amber-50/30 to-white" data-id="element-17664">
          <SectionHeader icon={<TargetIcon className="w-5 h-5" data-id="element-17666" />} title="Рекомендации на год" subtitle="Практические советы по использованию энергий" data-id="element-17665" />

          <div className="space-y-4" data-id="element-17667">
            {SOLAR_RECOMMENDATIONS.map((rec, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: i * 0.1
          }} className="bg-white rounded-xl p-5 border border-amber-200" data-id="element-17668">
                <h4 className="font-bold text-slate-900 mb-2 flex items-center gap-2" data-id="element-17669">
                  <span className="w-6 h-6 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center text-xs font-bold" data-id="element-17670">
                    {i + 1}
                  </span>
                  {rec.title}
                </h4>
                <p className="text-sm text-slate-600 leading-relaxed" data-id="element-17671">
                  {rec.text}
                </p>
              </motion.div>)}
          </div>
        </div>

        {/* ══════════ 16. ИТОГ ПРОГНОЗА ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-17672">
          <SectionHeader icon={<CompassIcon className="w-5 h-5" data-id="element-17674" />} title="Итог прогноза" subtitle="Резюме вашего солярного года" data-id="element-17673" />

          <div className="space-y-4 text-slate-600 leading-relaxed mb-6" data-id="element-17675">
            <p data-id="element-17676">{SOLAR_SUMMARY.overview}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-17677">
            <div className="bg-emerald-50 rounded-xl p-5 border border-emerald-100" data-id="element-17678">
              <h4 className="font-bold text-emerald-800 mb-3 flex items-center gap-2" data-id="element-17679">
                <TrendingUpIcon className="w-4 h-4" data-id="element-17680" /> Главные возможности
              </h4>
              <p className="text-sm text-emerald-700 leading-relaxed" data-id="element-17681">
                {SOLAR_SUMMARY.opportunities}
              </p>
            </div>
            <div className="bg-indigo-50 rounded-xl p-5 border border-indigo-100" data-id="element-17682">
              <h4 className="font-bold text-indigo-800 mb-3 flex items-center gap-2" data-id="element-17683">
                <BookOpenIcon className="w-4 h-4" data-id="element-17684" /> Ключевые уроки
              </h4>
              <p className="text-sm text-indigo-700 leading-relaxed" data-id="element-17685">
                {SOLAR_SUMMARY.lessons}
              </p>
            </div>
          </div>
        </div>
      </>;
  };
  // ═══════════════════════════════════════════
  // RENDER COMPATIBILITY CONTENT
  // ═══════════════════════════════════════════
  const renderCompatibilityContent = () => {
    const SectionHeader = ({
      icon,
      title,
      subtitle
    }: {
      icon: React.ReactNode;
      title: string;
      subtitle?: string;
    }) => <div className="flex items-center gap-3 mb-4 sm:mb-6" data-id="element-17686">
        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-rose-100 text-rose-600 flex items-center justify-center" data-id="element-17687">
          {icon}
        </div>
        <div className="min-w-0" data-id="element-17688">
          <h2 className="text-lg sm:text-xl font-bold text-slate-900 leading-tight" data-id="element-17689">
            {title}
          </h2>
          {subtitle && <p className="text-xs sm:text-sm text-slate-500" data-id="element-17690">{subtitle}</p>}
        </div>
      </div>;
    const StrengthsCautions = ({
      strengths,
      cautions,
      strengthsTitle,
      cautionsTitle
    }: {
      strengths: string[];
      cautions: string[];
      strengthsTitle?: string;
      cautionsTitle?: string;
    }) => <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6" data-id="element-17691">
        <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100" data-id="element-17692">
          <h4 className="font-bold text-emerald-800 mb-3 flex items-center gap-2" data-id="element-17693">
            <ShieldIcon className="w-4 h-4" data-id="element-17694" />{' '}
            {strengthsTitle || 'Сильные стороны'}
          </h4>
          <ul className="space-y-1.5 text-sm text-emerald-700" data-id="element-17695">
            {strengths.map((s, i) => <li key={i} className="flex items-start gap-2" data-id="element-17696">
                <CheckIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" data-id="element-17697" />
                {s}
              </li>)}
          </ul>
        </div>
        <div className="bg-amber-50 rounded-xl p-4 border border-amber-100" data-id="element-17698">
          <h4 className="font-bold text-amber-800 mb-3 flex items-center gap-2" data-id="element-17699">
            <AlertTriangleIcon className="w-4 h-4" data-id="element-17700" />{' '}
            {cautionsTitle || 'Зоны внимания'}
          </h4>
          <ul className="space-y-1.5 text-sm text-amber-700" data-id="element-17701">
            {cautions.map((c, i) => <li key={i} className="flex items-start gap-2" data-id="element-17702">
                <AlertTriangleIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" data-id="element-17703" />
                {c}
              </li>)}
          </ul>
        </div>
      </div>;
    const houseIconMap: Record<string, React.ReactNode> = {
      sun: <SunIcon className="w-5 h-5" data-id="element-17704" />,
      moon: <MoonIcon className="w-5 h-5" data-id="element-17705" />,
      heart: <HeartIcon className="w-5 h-5" data-id="element-17706" />,
      zap: <ZapIcon className="w-5 h-5" data-id="element-17707" />,
      sparkles: <SparklesIcon className="w-5 h-5" data-id="element-17708" />,
      compass: <CompassIcon className="w-5 h-5" data-id="element-17709" />,
      home: <StarIcon className="w-5 h-5" data-id="element-17710" />
    };
    const houseColorMap: Record<string, {
      bg: string;
      border: string;
      iconBg: string;
    }> = {
      amber: {
        bg: 'bg-amber-50',
        border: 'border-amber-200',
        iconBg: 'bg-amber-100 text-amber-600'
      },
      indigo: {
        bg: 'bg-indigo-50',
        border: 'border-indigo-200',
        iconBg: 'bg-indigo-100 text-indigo-600'
      },
      rose: {
        bg: 'bg-rose-50',
        border: 'border-rose-200',
        iconBg: 'bg-rose-100 text-rose-600'
      },
      orange: {
        bg: 'bg-orange-50',
        border: 'border-orange-200',
        iconBg: 'bg-orange-100 text-orange-600'
      },
      purple: {
        bg: 'bg-purple-50',
        border: 'border-purple-200',
        iconBg: 'bg-purple-100 text-purple-600'
      },
      emerald: {
        bg: 'bg-emerald-50',
        border: 'border-emerald-200',
        iconBg: 'bg-emerald-100 text-emerald-600'
      },
      pink: {
        bg: 'bg-pink-50',
        border: 'border-pink-200',
        iconBg: 'bg-pink-100 text-pink-600'
      }
    };
    const strengthIconMap: Record<string, React.ReactNode> = {
      heart: <HeartIcon className="w-4 h-4" data-id="element-17711" />,
      sparkles: <SparklesIcon className="w-4 h-4" data-id="element-17712" />,
      zap: <ZapIcon className="w-4 h-4" data-id="element-17713" />,
      home: <StarIcon className="w-4 h-4" data-id="element-17714" />,
      compass: <CompassIcon className="w-4 h-4" data-id="element-17715" />
    };
    const strengthColorMap: Record<string, {
      bg: string;
      border: string;
      iconBg: string;
    }> = {
      rose: {
        bg: 'bg-rose-50',
        border: 'border-rose-200',
        iconBg: 'bg-rose-100 text-rose-600'
      },
      amber: {
        bg: 'bg-amber-50',
        border: 'border-amber-200',
        iconBg: 'bg-amber-100 text-amber-600'
      },
      pink: {
        bg: 'bg-pink-50',
        border: 'border-pink-200',
        iconBg: 'bg-pink-100 text-pink-600'
      },
      emerald: {
        bg: 'bg-emerald-50',
        border: 'border-emerald-200',
        iconBg: 'bg-emerald-100 text-emerald-600'
      },
      purple: {
        bg: 'bg-purple-50',
        border: 'border-purple-200',
        iconBg: 'bg-purple-100 text-purple-600'
      }
    };
    return <>
        {/* ══════════ 01. ВВЕДЕНИЕ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-17716">
          <SectionHeader icon={<BookOpenIcon className="w-5 h-5" data-id="element-17718" />} title={COMPAT_INTRO.title} subtitle="Что такое синастрия и как работает анализ" data-id="element-17717" />
          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-17719">
            <p data-id="element-17720">{COMPAT_INTRO.whatIsSynastry}</p>
            <p data-id="element-17721">{COMPAT_INTRO.howItWorks}</p>
            <p data-id="element-17722">{COMPAT_INTRO.whatIsAnalyzed}</p>
          </div>
        </div>

        {/* ══════════ 02. ОБЩАЯ ДИНАМИКА ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-rose-50/50 to-white" data-id="element-17723">
          <SectionHeader icon={<HeartIcon className="w-5 h-5" data-id="element-17725" />} title={COMPAT_OVERALL_DYNAMICS.title} subtitle="Энергетика и потенциал вашего союза" data-id="element-17724" />

          {/* Compatibility Scores */}
          <div className="space-y-4 mb-6" data-id="element-17726">
            {COMPATIBILITY_SCORES.map((item, i) => <div key={i} data-id="element-17727">
                <div className="flex justify-between text-sm mb-1.5" data-id="element-17728">
                  <span className="font-medium text-slate-700" data-id="element-17729">
                    {item.area}
                  </span>
                  <span className={`font-bold ${item.percent >= 80 ? 'text-emerald-600' : item.percent >= 60 ? 'text-amber-600' : 'text-rose-600'}`} data-id="element-17730">
                    {item.percent}%
                  </span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden" data-id="element-17731">
                  <motion.div className={`${item.color} h-3 rounded-full`} initial={{
                width: 0
              }} animate={{
                width: `${item.percent}%`
              }} transition={{
                duration: 1,
                delay: i * 0.15,
                ease: 'easeOut'
              }} data-id="element-17732" />
                </div>
              </div>)}
          </div>

          <div className="bg-rose-100/50 rounded-xl p-4 border border-rose-200 mb-6" data-id="element-17733">
            <div className="text-center" data-id="element-17734">
              <div className="text-3xl font-bold text-rose-600 mb-1" data-id="element-17735">83%</div>
              <div className="text-sm text-rose-700" data-id="element-17736">
                Общий индекс совместимости
              </div>
            </div>
          </div>

          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-17737">
            <p data-id="element-17738">{COMPAT_OVERALL_DYNAMICS.overallEnergy}</p>
            <p data-id="element-17739">{COMPAT_OVERALL_DYNAMICS.potential}</p>
          </div>

          <StrengthsCautions strengths={COMPAT_OVERALL_DYNAMICS.strengths} cautions={COMPAT_OVERALL_DYNAMICS.challenges} cautionsTitle="Ключевые вызовы" data-id="element-17740" />
        </div>

        {/* ══════════ 03. СОЛНЦЕ И ЛИЧНОСТЬ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-17741">
          <SectionHeader icon={<SunIcon className="w-5 h-5" data-id="element-17743" />} title={COMPAT_SUN_INTERACTION.title} subtitle={COMPAT_SUN_INTERACTION.subtitle} data-id="element-17742" />

          {/* Two People's Big Three */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6" data-id="element-17744">
            <div className="bg-rose-50/50 rounded-xl p-4 border border-rose-100" data-id="element-17745">
              <div className="text-sm font-bold text-rose-600 mb-3" data-id="element-17746">
                Алексей
              </div>
              <div className="space-y-2" data-id="element-17747">
                <div className="flex justify-between text-sm" data-id="element-17748">
                  <span className="text-slate-500" data-id="element-17749">☉ Солнце</span>
                  <span className="font-medium text-slate-700" data-id="element-17750">♌ Лев</span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-17751">
                  <span className="text-slate-500" data-id="element-17752">☽ Луна</span>
                  <span className="font-medium text-slate-700" data-id="element-17753">
                    ♏ Скорпион
                  </span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-17754">
                  <span className="text-slate-500" data-id="element-17755">ASC</span>
                  <span className="font-medium text-slate-700" data-id="element-17756">♐ Стрелец</span>
                </div>
              </div>
            </div>
            <div className="bg-sky-50/50 rounded-xl p-4 border border-sky-100" data-id="element-17757">
              <div className="text-sm font-bold text-sky-600 mb-3" data-id="element-17758">Мария</div>
              <div className="space-y-2" data-id="element-17759">
                <div className="flex justify-between text-sm" data-id="element-17760">
                  <span className="text-slate-500" data-id="element-17761">☉ Солнце</span>
                  <span className="font-medium text-slate-700" data-id="element-17762">♓ Рыбы</span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-17763">
                  <span className="text-slate-500" data-id="element-17764">☽ Луна</span>
                  <span className="font-medium text-slate-700" data-id="element-17765">♋ Рак</span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-17766">
                  <span className="text-slate-500" data-id="element-17767">ASC</span>
                  <span className="font-medium text-slate-700" data-id="element-17768">♎ Весы</span>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-17769">
            <p data-id="element-17770">{COMPAT_SUN_INTERACTION.description}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-6" data-id="element-17771">
              <Card padding="md" className="bg-white border-amber-200" data-id="element-17772">
                <h4 className="text-xs font-bold text-amber-600 uppercase tracking-wider mb-2" data-id="element-17773">
                  Алексей (♌ Лев)
                </h4>
                <p className="text-sm text-slate-600" data-id="element-17774">
                  {COMPAT_SUN_INTERACTION.partner1Goals}
                </p>
              </Card>
              <Card padding="md" className="bg-white border-indigo-200" data-id="element-17775">
                <h4 className="text-xs font-bold text-indigo-600 uppercase tracking-wider mb-2" data-id="element-17776">
                  Мария (♓ Рыбы)
                </h4>
                <p className="text-sm text-slate-600" data-id="element-17777">
                  {COMPAT_SUN_INTERACTION.partner2Goals}
                </p>
              </Card>
            </div>
            <p data-id="element-17778">
              <strong className="text-slate-800" data-id="element-17779">Взаимное понимание:</strong>{' '}
              {COMPAT_SUN_INTERACTION.mutualUnderstanding}
            </p>
          </div>
        </div>

        {/* ══════════ 04. ЭМОЦИОНАЛЬНАЯ СОВМЕСТИМОСТЬ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-indigo-50/30 to-white" data-id="element-17780">
          <SectionHeader icon={<MoonIcon className="w-5 h-5" data-id="element-17782" />} title={COMPAT_EMOTIONAL.title} subtitle={COMPAT_EMOTIONAL.subtitle} data-id="element-17781" />

          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-17783">
            <p data-id="element-17784">{COMPAT_EMOTIONAL.description}</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-6" data-id="element-17785">
              <Card padding="md" className="bg-white border-rose-200" data-id="element-17786">
                <h4 className="text-xs font-bold text-rose-600 uppercase tracking-wider mb-2" data-id="element-17787">
                  Алексей (☽ Скорпион)
                </h4>
                <p className="text-sm text-slate-600" data-id="element-17788">
                  {COMPAT_EMOTIONAL.partner1Needs}
                </p>
              </Card>
              <Card padding="md" className="bg-white border-sky-200" data-id="element-17789">
                <h4 className="text-xs font-bold text-sky-600 uppercase tracking-wider mb-2" data-id="element-17790">
                  Мария (☽ Рак)
                </h4>
                <p className="text-sm text-slate-600" data-id="element-17791">
                  {COMPAT_EMOTIONAL.partner2Needs}
                </p>
              </Card>
            </div>

            <p data-id="element-17792">
              <strong className="text-slate-800" data-id="element-17793">
                Эмоциональная поддержка:
              </strong>{' '}
              {COMPAT_EMOTIONAL.emotionalSupport}
            </p>
          </div>

          <StrengthsCautions strengths={COMPAT_EMOTIONAL.strengths} cautions={COMPAT_EMOTIONAL.cautions} data-id="element-17794" />
        </div>

        {/* ══════════ 05. РОМАНТИЧЕСКАЯ СОВМЕСТИМОСТЬ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-17795">
          <SectionHeader icon={<HeartIcon className="w-5 h-5" data-id="element-17797" />} title={COMPAT_ROMANTIC.title} subtitle={COMPAT_ROMANTIC.subtitle} data-id="element-17796" />

          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-17798">
            <p data-id="element-17799">{COMPAT_ROMANTIC.description}</p>
            <p data-id="element-17800">
              <strong className="text-slate-800" data-id="element-17801">Романтическая химия:</strong>{' '}
              {COMPAT_ROMANTIC.chemistry}
            </p>
            <p data-id="element-17802">{COMPAT_ROMANTIC.loveStyles}</p>
          </div>

          <StrengthsCautions strengths={COMPAT_ROMANTIC.strengths} cautions={COMPAT_ROMANTIC.cautions} data-id="element-17803" />
        </div>

        {/* ══════════ 06. ИНТЕЛЛЕКТУАЛЬНАЯ СОВМЕСТИМОСТЬ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-sky-50/30 to-white" data-id="element-17804">
          <SectionHeader icon={<MessageCircleIcon className="w-5 h-5" data-id="element-17806" />} title={COMPAT_INTELLECTUAL.title} subtitle={COMPAT_INTELLECTUAL.subtitle} data-id="element-17805" />

          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-17807">
            <p data-id="element-17808">{COMPAT_INTELLECTUAL.description}</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-6" data-id="element-17809">
              <Card padding="md" className="bg-white border-amber-200" data-id="element-17810">
                <h4 className="text-xs font-bold text-amber-600 uppercase tracking-wider mb-2" data-id="element-17811">
                  Стиль Алексея (☿ Лев)
                </h4>
                <p className="text-sm text-slate-600" data-id="element-17812">
                  {COMPAT_INTELLECTUAL.partner1Style}
                </p>
              </Card>
              <Card padding="md" className="bg-white border-indigo-200" data-id="element-17813">
                <h4 className="text-xs font-bold text-indigo-600 uppercase tracking-wider mb-2" data-id="element-17814">
                  Стиль Марии (☿ Рыбы)
                </h4>
                <p className="text-sm text-slate-600" data-id="element-17815">
                  {COMPAT_INTELLECTUAL.partner2Style}
                </p>
              </Card>
            </div>

            <p data-id="element-17816">{COMPAT_INTELLECTUAL.communicationDynamics}</p>
          </div>

          <div className="mt-6 bg-amber-50 rounded-xl p-4 border border-amber-200" data-id="element-17817">
            <h4 className="font-bold text-amber-800 mb-3 flex items-center gap-2" data-id="element-17818">
              <AlertTriangleIcon className="w-4 h-4" data-id="element-17819" /> Возможные сложности
            </h4>
            <ul className="space-y-1.5 text-sm text-amber-700" data-id="element-17820">
              {COMPAT_INTELLECTUAL.difficulties.map((d, i) => <li key={i} className="flex items-start gap-2" data-id="element-17821">
                  <AlertTriangleIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" data-id="element-17822" />
                  {d}
                </li>)}
            </ul>
          </div>
        </div>

        {/* ══════════ 07. КАРМИЧЕСКИЕ СВЯЗИ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-17823">
          <SectionHeader icon={<CompassIcon className="w-5 h-5" data-id="element-17825" />} title={COMPAT_KARMIC.title} subtitle={COMPAT_KARMIC.subtitle} data-id="element-17824" />

          <p className="text-slate-600 leading-relaxed mb-6" data-id="element-17826">
            {COMPAT_KARMIC.description}
          </p>

          <div className="space-y-6" data-id="element-17827">
            {[COMPAT_KARMIC.saturnAnalysis, COMPAT_KARMIC.plutoAnalysis, COMPAT_KARMIC.nodesAnalysis].map((analysis, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: idx * 0.1
          }} className="bg-white rounded-xl p-5 border border-purple-200" data-id="element-17828">
                <h4 className="font-bold text-slate-900 mb-3 flex items-center gap-2" data-id="element-17829">
                  <span className="w-6 h-6 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-xs font-bold" data-id="element-17830">
                    {idx + 1}
                  </span>
                  {analysis.title}
                </h4>
                <p className="text-sm text-slate-600 leading-relaxed" data-id="element-17831">
                  {analysis.text}
                </p>
              </motion.div>)}
          </div>
        </div>

        {/* ══════════ 08. ПЛАНЕТЫ В ДОМАХ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-rose-50/30 to-white" data-id="element-17832">
          <SectionHeader icon={<LayersIcon className="w-5 h-5" data-id="element-17834" />} title="Планеты партнёра в домах другого" subtitle="Как вы влияете на жизнь друг друга" data-id="element-17833" />

          <div className="space-y-4" data-id="element-17835">
            {COMPAT_PLANETS_IN_HOUSES.map((item, idx) => {
            const colors = houseColorMap[item.color] || houseColorMap.amber;
            return <motion.div key={idx} initial={{
              opacity: 0,
              x: -10
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: idx * 0.08
            }} className={`${colors.bg} rounded-xl p-5 border ${colors.border}`} data-id="element-17836">
                  <div className="flex items-center gap-3 mb-3" data-id="element-17837">
                    <div className={`w-8 h-8 rounded-lg ${colors.iconBg} flex items-center justify-center`} data-id="element-17838">
                      {houseIconMap[item.icon] || <StarIcon className="w-4 h-4" data-id="element-17839" />}
                    </div>
                    <h4 className="font-bold text-slate-900" data-id="element-17840">{item.title}</h4>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed" data-id="element-17841">
                    {item.text}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>

        {/* ══════════ 09. АСПЕКТЫ МЕЖДУ КАРТАМИ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-17842">
          <SectionHeader icon={<SparklesIcon className="w-5 h-5" data-id="element-17844" />} title="Основные аспекты между картами" subtitle="Взаимодействие планетарных энергий" data-id="element-17843" />

          <div className="space-y-4" data-id="element-17845">
            {COMPAT_ASPECTS_DETAILED.map((aspect, i) => {
            const isHarmonious = aspect.nature === 'harmonious';
            const isIntense = aspect.nature === 'intense';
            return <motion.div key={i} initial={{
              opacity: 0,
              x: -10
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: i * 0.08
            }} className={`rounded-xl p-5 border ${isHarmonious ? 'bg-emerald-50/50 border-emerald-200' : isIntense ? 'bg-purple-50/50 border-purple-200' : 'bg-rose-50/50 border-rose-200'}`} data-id="element-17846">
                  <div className="flex flex-wrap items-start sm:items-center justify-between gap-2 sm:gap-3 mb-3" data-id="element-17847">
                    <div className="flex items-center gap-2 sm:gap-3" data-id="element-17848">
                      <span className={`text-lg font-bold ${isHarmonious ? 'text-emerald-600' : isIntense ? 'text-purple-600' : 'text-rose-600'}`} data-id="element-17849">
                        {aspect.aspect}
                      </span>
                      <h4 className="font-bold text-slate-900 text-sm sm:text-base" data-id="element-17850">
                        {aspect.title}
                      </h4>
                    </div>
                    <Badge variant={isHarmonious ? 'success' : isIntense ? 'pro' : 'warning'} className="text-xs" data-id="element-17851">
                      {isHarmonious ? 'Гармоничный' : isIntense ? 'Интенсивный' : 'Напряжённый'}
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed" data-id="element-17852">
                    {aspect.description}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>

        {/* ══════════ 10. СИЛЬНЫЕ СТОРОНЫ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-emerald-50/30 to-white" data-id="element-17853">
          <SectionHeader icon={<ShieldIcon className="w-5 h-5" data-id="element-17855" />} title={COMPAT_STRENGTHS.title} subtitle={COMPAT_STRENGTHS.description} data-id="element-17854" />

          <div className="space-y-4" data-id="element-17856">
            {COMPAT_STRENGTHS.items.map((item, idx) => {
            const colors = strengthColorMap[item.color] || strengthColorMap.rose;
            return <motion.div key={idx} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: idx * 0.08
            }} className={`${colors.bg} rounded-xl p-5 border ${colors.border}`} data-id="element-17857">
                  <div className="flex items-center gap-3 mb-3" data-id="element-17858">
                    <div className={`w-8 h-8 rounded-lg ${colors.iconBg} flex items-center justify-center`} data-id="element-17859">
                      {strengthIconMap[item.icon] || <StarIcon className="w-4 h-4" data-id="element-17860" />}
                    </div>
                    <h4 className="font-bold text-slate-900" data-id="element-17861">{item.title}</h4>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed" data-id="element-17862">
                    {item.text}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>

        {/* ══════════ 11. ВОЗМОЖНЫЕ КОНФЛИКТЫ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-17863">
          <SectionHeader icon={<AlertTriangleIcon className="w-5 h-5" data-id="element-17865" />} title={COMPAT_CONFLICTS.title} subtitle={COMPAT_CONFLICTS.description} data-id="element-17864" />

          <div className="space-y-4" data-id="element-17866">
            {COMPAT_CONFLICTS.items.map((item, idx) => {
            const severityColors = {
              high: {
                bg: 'bg-rose-50',
                border: 'border-rose-200',
                badge: 'warning' as const,
                label: 'Высокий'
              },
              medium: {
                bg: 'bg-amber-50',
                border: 'border-amber-200',
                badge: 'neutral' as const,
                label: 'Средний'
              },
              low: {
                bg: 'bg-slate-50',
                border: 'border-slate-200',
                badge: 'success' as const,
                label: 'Низкий'
              }
            };
            const sev = severityColors[item.severity];
            return <motion.div key={idx} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: idx * 0.1
            }} className={`${sev.bg} rounded-xl p-5 border ${sev.border}`} data-id="element-17867">
                  <div className="flex flex-wrap items-start sm:items-center justify-between gap-2 sm:gap-3 mb-3" data-id="element-17868">
                    <h4 className="font-bold text-slate-900 text-sm sm:text-base" data-id="element-17869">
                      {item.title}
                    </h4>
                    <Badge variant={sev.badge} className="text-xs" data-id="element-17870">
                      Риск: {sev.label}
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed mb-3" data-id="element-17871">
                    {item.text}
                  </p>
                  <div className="bg-white/60 rounded-lg p-3 border border-white/80" data-id="element-17872">
                    <p className="text-xs text-slate-700" data-id="element-17873">
                      <strong className="text-emerald-700" data-id="element-17874">Решение:</strong>{' '}
                      {item.solution}
                    </p>
                  </div>
                </motion.div>;
          })}
          </div>
        </div>

        {/* ══════════ 12. ДОЛГОСРОЧНЫЙ ПОТЕНЦИАЛ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-purple-50/30 to-white" data-id="element-17875">
          <SectionHeader icon={<TrendingUpIcon className="w-5 h-5" data-id="element-17877" />} title={COMPAT_LONGTERM.title} subtitle={`Общий потенциал: ${COMPAT_LONGTERM.overallScore}%`} data-id="element-17876" />

          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-17878">
            <p data-id="element-17879">{COMPAT_LONGTERM.description}</p>
            <p data-id="element-17880">
              <strong className="text-slate-800" data-id="element-17881">Устойчивость союза:</strong>{' '}
              {COMPAT_LONGTERM.sustainability}
            </p>
            <p data-id="element-17882">
              <strong className="text-slate-800" data-id="element-17883">Совместные цели:</strong>{' '}
              {COMPAT_LONGTERM.sharedGoals}
            </p>
          </div>

          <div className="mt-6 bg-purple-100/50 rounded-xl p-4 border border-purple-200" data-id="element-17884">
            <h4 className="font-bold text-purple-800 mb-3" data-id="element-17885">
              Направления развития отношений
            </h4>
            <ul className="space-y-1.5 text-sm text-purple-700" data-id="element-17886">
              {COMPAT_LONGTERM.developmentAreas.map((area, i) => <li key={i} className="flex items-start gap-2" data-id="element-17887">
                  <TrendingUpIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" data-id="element-17888" />
                  {area}
                </li>)}
            </ul>
          </div>
        </div>

        {/* ══════════ 13. РЕКОМЕНДАЦИИ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-17889">
          <SectionHeader icon={<TargetIcon className="w-5 h-5" data-id="element-17891" />} title="Практические рекомендации" subtitle="Советы для укрепления отношений" data-id="element-17890" />

          <div className="space-y-4" data-id="element-17892">
            {COMPAT_RECOMMENDATIONS.map((rec, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: i * 0.1
          }} className="bg-white rounded-xl p-5 border border-rose-200" data-id="element-17893">
                <h4 className="font-bold text-slate-900 mb-2 flex items-center gap-2" data-id="element-17894">
                  <span className="w-6 h-6 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center text-xs font-bold" data-id="element-17895">
                    {i + 1}
                  </span>
                  {rec.title}
                </h4>
                <p className="text-sm text-slate-600 leading-relaxed" data-id="element-17896">
                  {rec.text}
                </p>
              </motion.div>)}
          </div>
        </div>

        {/* ══════════ 14. ИТОГ АНАЛИЗА ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-rose-50/30 to-white" data-id="element-17897">
          <SectionHeader icon={<CompassIcon className="w-5 h-5" data-id="element-17899" />} title="Итог анализа" subtitle="Резюме совместимости" data-id="element-17898" />

          <p className="text-slate-600 leading-relaxed mb-6" data-id="element-17900">
            {COMPAT_SUMMARY.overview}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6" data-id="element-17901">
            <div className="bg-emerald-50 rounded-xl p-5 border border-emerald-100" data-id="element-17902">
              <h4 className="font-bold text-emerald-800 mb-3 flex items-center gap-2" data-id="element-17903">
                <CheckIcon className="w-4 h-4" data-id="element-17904" /> Главные плюсы
              </h4>
              <ul className="space-y-1.5 text-sm text-emerald-700" data-id="element-17905">
                {COMPAT_SUMMARY.mainPros.map((p, i) => <li key={i} className="flex items-start gap-2" data-id="element-17906">
                    <CheckIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" data-id="element-17907" />
                    {p}
                  </li>)}
              </ul>
            </div>
            <div className="bg-indigo-50 rounded-xl p-5 border border-indigo-100" data-id="element-17908">
              <h4 className="font-bold text-indigo-800 mb-3 flex items-center gap-2" data-id="element-17909">
                <BookOpenIcon className="w-4 h-4" data-id="element-17910" /> Ключевые уроки
              </h4>
              <ul className="space-y-1.5 text-sm text-indigo-700" data-id="element-17911">
                {COMPAT_SUMMARY.keyLessons.map((l, i) => <li key={i} className="flex items-start gap-2" data-id="element-17912">
                    <CompassIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" data-id="element-17913" />
                    {l}
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </>;
  };
  // ═══════════════════════════════════════════
  // RENDER COMBO CONTENT
  // ═══════════════════════════════════════════
  const renderComboContent = () => {
    const SectionHeader = ({
      icon,
      title,
      subtitle,
      color = 'purple'
    }: {
      icon: React.ReactNode;
      title: string;
      subtitle?: string;
      color?: string;
    }) => <div className="flex items-center gap-3 mb-4 sm:mb-6" data-id="element-17914">
        <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-${color}-100 text-${color}-600 flex items-center justify-center`} data-id="element-17915">
          {icon}
        </div>
        <div className="min-w-0" data-id="element-17916">
          <h2 className="text-lg sm:text-xl font-bold text-slate-900 leading-tight" data-id="element-17917">
            {title}
          </h2>
          {subtitle && <p className="text-xs sm:text-sm text-slate-500" data-id="element-17918">{subtitle}</p>}
        </div>
      </div>;
    const PartDivider = ({
      part,
      title,
      subtitle
    }: {
      part: number;
      title: string;
      subtitle: string;
    }) => <div className={`p-4 sm:p-6 md:p-8 border-b border-slate-100 ${part === 1 ? 'bg-gradient-to-r from-indigo-600 to-purple-600' : 'bg-gradient-to-r from-amber-600 to-orange-600'} text-white`} data-id="element-17919">
        <div className="flex items-center gap-3 sm:gap-4" data-id="element-17920">
          <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-white/20 flex items-center justify-center text-xl sm:text-2xl font-bold flex-shrink-0" data-id="element-17921">
            {part}
          </div>
          <div className="min-w-0" data-id="element-17922">
            <h2 className="text-lg sm:text-2xl font-bold" data-id="element-17923">{title}</h2>
            <p className="text-white/70 text-xs sm:text-sm" data-id="element-17924">{subtitle}</p>
          </div>
        </div>
      </div>;
    const lifeAreaIcons: Record<string, React.ReactNode> = {
      briefcase: <BriefcaseIcon className="w-5 h-5" data-id="element-17925" />,
      heart: <HeartIcon className="w-5 h-5" data-id="element-17926" />,
      'trending-up': <TrendingUpIcon className="w-5 h-5" data-id="element-17927" />,
      compass: <CompassIcon className="w-5 h-5" data-id="element-17928" />,
      users: <UsersIcon className="w-5 h-5" data-id="element-17929" />,
      activity: <ActivityIcon className="w-5 h-5" data-id="element-17930" />
    };
    const lifeAreaColors: Record<string, {
      bg: string;
      border: string;
      iconBg: string;
    }> = {
      emerald: {
        bg: 'bg-emerald-50',
        border: 'border-emerald-200',
        iconBg: 'bg-emerald-100 text-emerald-600'
      },
      rose: {
        bg: 'bg-rose-50',
        border: 'border-rose-200',
        iconBg: 'bg-rose-100 text-rose-600'
      },
      indigo: {
        bg: 'bg-indigo-50',
        border: 'border-indigo-200',
        iconBg: 'bg-indigo-100 text-indigo-600'
      },
      teal: {
        bg: 'bg-teal-50',
        border: 'border-teal-200',
        iconBg: 'bg-teal-100 text-teal-600'
      },
      sky: {
        bg: 'bg-sky-50',
        border: 'border-sky-200',
        iconBg: 'bg-sky-100 text-sky-600'
      }
    };
    return <>
        {/* ═══════════════════════════════════ */}
        {/* PART 1 — NATAL CHART */}
        {/* ═══════════════════════════════════ */}
        <PartDivider part={1} title="Натальная карта" subtitle="Анализ личности, потенциала и жизненных особенностей" data-id="element-17931" />

        {/* ══════════ 01. ВВЕДЕНИЕ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-17932">
          <SectionHeader icon={<BookOpenIcon className="w-5 h-5" data-id="element-17934" />} title={COMBO_NATAL_INTRO.title} subtitle="Что такое натальная карта и что она показывает" color="indigo" data-id="element-17933" />
          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-17935">
            <p data-id="element-17936">{COMBO_NATAL_INTRO.whatIsNatalChart}</p>
            <p data-id="element-17937">{COMBO_NATAL_INTRO.whatItShows}</p>
            <p data-id="element-17938">{COMBO_NATAL_INTRO.whatIsAnalyzed}</p>
          </div>
        </div>

        {/* ══════════ 02. ОБЩАЯ ХАРАКТЕРИСТИКА ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-indigo-50/50 to-white" data-id="element-17939">
          <SectionHeader icon={<SparklesIcon className="w-5 h-5" data-id="element-17941" />} title={COMBO_PERSONALITY.title} subtitle="Базовые черты, мотивации и ценности" color="indigo" data-id="element-17940" />

          <p className="text-slate-600 leading-relaxed mb-6" data-id="element-17942">
            {COMBO_PERSONALITY.overview}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6" data-id="element-17943">
            {COMBO_PERSONALITY.traits.map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: i * 0.1
          }} className="bg-white rounded-xl p-4 border border-indigo-200" data-id="element-17944">
                <h4 className="font-bold text-indigo-800 mb-2" data-id="element-17945">{item.trait}</h4>
                <p className="text-sm text-slate-600" data-id="element-17946">{item.description}</p>
              </motion.div>)}
          </div>

          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-17947">
            <p data-id="element-17948">
              <strong className="text-slate-800" data-id="element-17949">Мотивации:</strong>{' '}
              {COMBO_PERSONALITY.motivations}
            </p>
            <p data-id="element-17950">
              <strong className="text-slate-800" data-id="element-17951">Ценности:</strong>{' '}
              {COMBO_PERSONALITY.values}
            </p>
          </div>
        </div>

        {/* ══════════ 03. СОЛНЦЕ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-17952">
          <SectionHeader icon={<SunIcon className="w-5 h-5" data-id="element-17954" />} title={COMBO_SUN.title} subtitle={COMBO_SUN.subtitle} color="amber" data-id="element-17953" />
          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-17955">
            <p data-id="element-17956">{COMBO_SUN.description}</p>
            <p data-id="element-17957">
              <strong className="text-slate-800" data-id="element-17958">Жизненные цели:</strong>{' '}
              {COMBO_SUN.lifeGoals}
            </p>
            <p data-id="element-17959">
              <strong className="text-slate-800" data-id="element-17960">Проявление личности:</strong>{' '}
              {COMBO_SUN.personality}
            </p>
            <p data-id="element-17961">
              <strong className="text-slate-800" data-id="element-17962">Внутренний потенциал:</strong>{' '}
              {COMBO_SUN.potential}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6" data-id="element-17963">
            <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100" data-id="element-17964">
              <h4 className="font-bold text-emerald-800 mb-3 flex items-center gap-2" data-id="element-17965">
                <ShieldIcon className="w-4 h-4" data-id="element-17966" /> Суперсилы
              </h4>
              <ul className="space-y-1.5 text-sm text-emerald-700" data-id="element-17967">
                {COMBO_SUN.strengths.map((s, i) => <li key={i} className="flex items-start gap-2" data-id="element-17968">
                    <CheckIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" data-id="element-17969" />
                    {s}
                  </li>)}
              </ul>
            </div>
            <div className="bg-rose-50 rounded-xl p-4 border border-rose-100" data-id="element-17970">
              <h4 className="font-bold text-rose-800 mb-3 flex items-center gap-2" data-id="element-17971">
                <ZapIcon className="w-4 h-4" data-id="element-17972" /> Теневая сторона
              </h4>
              <ul className="space-y-1.5 text-sm text-rose-700" data-id="element-17973">
                {COMBO_SUN.shadows.map((s, i) => <li key={i} className="flex items-start gap-2" data-id="element-17974">
                    <AlertTriangleIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" data-id="element-17975" />
                    {s}
                  </li>)}
              </ul>
            </div>
          </div>
        </div>

        {/* ══════════ 04. ЛУНА ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-indigo-50/30 to-white" data-id="element-17976">
          <SectionHeader icon={<MoonIcon className="w-5 h-5" data-id="element-17978" />} title={COMBO_MOON.title} subtitle={COMBO_MOON.subtitle} color="indigo" data-id="element-17977" />
          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-17979">
            <p data-id="element-17980">{COMBO_MOON.description}</p>
            <p data-id="element-17981">
              <strong className="text-slate-800" data-id="element-17982">Эмоциональные реакции:</strong>{' '}
              {COMBO_MOON.emotionalReactions}
            </p>
            <p data-id="element-17983">
              <strong className="text-slate-800" data-id="element-17984">
                Внутренние потребности:
              </strong>{' '}
              {COMBO_MOON.innerNeeds}
            </p>
            <p data-id="element-17985">
              <strong className="text-slate-800" data-id="element-17986">
                Способ переживания чувств:
              </strong>{' '}
              {COMBO_MOON.feelingStyle}
            </p>
          </div>
        </div>

        {/* ══════════ 05. АСЦЕНДЕНТ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-17987">
          <SectionHeader icon={<StarIcon className="w-5 h-5" data-id="element-17989" />} title={COMBO_ASCENDANT.title} subtitle={COMBO_ASCENDANT.subtitle} color="purple" data-id="element-17988" />
          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-17990">
            <p data-id="element-17991">{COMBO_ASCENDANT.description}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 my-6" data-id="element-17992">
              <Card padding="md" className="bg-white border-purple-200" data-id="element-17993">
                <h4 className="text-xs font-bold text-purple-600 uppercase tracking-wider mb-2" data-id="element-17994">
                  Внешнее проявление
                </h4>
                <p className="text-sm text-slate-600" data-id="element-17995">
                  {COMBO_ASCENDANT.externalPresentation}
                </p>
              </Card>
              <Card padding="md" className="bg-white border-amber-200" data-id="element-17996">
                <h4 className="text-xs font-bold text-amber-600 uppercase tracking-wider mb-2" data-id="element-17997">
                  Первое впечатление
                </h4>
                <p className="text-sm text-slate-600" data-id="element-17998">
                  {COMBO_ASCENDANT.firstImpression}
                </p>
              </Card>
              <Card padding="md" className="bg-white border-emerald-200" data-id="element-17999">
                <h4 className="text-xs font-bold text-emerald-600 uppercase tracking-wider mb-2" data-id="element-18000">
                  Стиль поведения
                </h4>
                <p className="text-sm text-slate-600" data-id="element-18001">
                  {COMBO_ASCENDANT.behaviorStyle}
                </p>
              </Card>
            </div>
          </div>
        </div>

        {/* ══════════ 06. ПЛАНЕТЫ В ЗНАКАХ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-purple-50/30 to-white" data-id="element-18002">
          <SectionHeader icon={<SparklesIcon className="w-5 h-5" data-id="element-18004" />} title="Планеты в знаках" subtitle="Влияние каждой планеты на вашу личность" color="purple" data-id="element-18003" />
          <div className="space-y-4" data-id="element-18005">
            {COMBO_PLANETS_IN_SIGNS.map((planet, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: idx * 0.06
          }} className="bg-white rounded-xl p-5 border border-slate-200" data-id="element-18006">
                <div className="flex items-center gap-3 mb-3" data-id="element-18007">
                  <div className="w-8 h-8 rounded-lg bg-purple-100 text-purple-600 flex items-center justify-center text-lg" data-id="element-18008">
                    {planet.symbol}
                  </div>
                  <div data-id="element-18009">
                    <h4 className="font-bold text-slate-900" data-id="element-18010">
                      {planet.planet} в {planet.sign}
                    </h4>
                    <p className="text-xs text-slate-500" data-id="element-18011">
                      {planet.house}-й дом
                    </p>
                  </div>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed mb-3" data-id="element-18012">
                  {planet.description}
                </p>
                <div className="flex flex-wrap gap-2" data-id="element-18013">
                  {planet.strengths.map((s, i) => <span key={i} className="text-xs bg-purple-50 text-purple-700 px-2.5 py-1 rounded-full border border-purple-100" data-id="element-18014">
                      {s}
                    </span>)}
                </div>
              </motion.div>)}
          </div>
        </div>

        {/* ══════════ 07. ПЛАНЕТЫ В ДОМАХ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-18015">
          <SectionHeader icon={<LayersIcon className="w-5 h-5" data-id="element-18017" />} title="Планеты в домах" subtitle="Активированные сферы жизни" color="indigo" data-id="element-18016" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-18018">
            {COMBO_HOUSES_SUMMARY.map((house, i) => <motion.div key={i} initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: i * 0.04
          }} className="bg-slate-50 rounded-xl p-4 border border-slate-200" data-id="element-18019">
                <div className="flex items-center gap-2 mb-2" data-id="element-18020">
                  <span className="w-7 h-7 rounded-lg bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-bold" data-id="element-18021">
                    {house.house}
                  </span>
                  <div data-id="element-18022">
                    <span className="text-sm font-bold text-slate-900" data-id="element-18023">
                      {house.theme}
                    </span>
                    <span className="text-xs text-slate-400 ml-2" data-id="element-18024">
                      {house.sign}
                    </span>
                  </div>
                </div>
                <p className="text-xs text-slate-600" data-id="element-18025">{house.text}</p>
              </motion.div>)}
          </div>
        </div>

        {/* ══════════ 08. АСПЕКТЫ НАТАЛЬНОЙ КАРТЫ (COMBO) ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 bg-white border-t border-slate-200" data-id="element-18026">
          <div className="max-w-5xl mx-auto" data-id="element-18027">
            <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-18028">
              <SparklesIcon className="w-5 h-5 text-indigo-500" data-id="element-18029" />
              Аспекты натальной карты
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-18030">
              {COMBO_NATAL_ASPECTS.map((aspect, i) => <motion.div key={i} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: i * 0.03
            }} className="bg-slate-50 rounded-xl p-4 border border-slate-200" data-id="element-18031">
                  <div className="flex items-center gap-2 mb-2" data-id="element-18032">
                    <span className="text-sm font-bold text-slate-900" data-id="element-18033">
                      {aspect.aspect}
                    </span>
                    <Badge variant="neutral" size="sm" className="text-[10px]" data-id="element-18034">
                      {aspect.type}
                    </Badge>
                  </div>
                  <p className="text-xs text-slate-600" data-id="element-18035">{aspect.text}</p>
                </motion.div>)}
            </div>
          </div>
        </div>

        {/* ══════════ 09. ЖИЗНЕННЫЕ СФЕРЫ (COMBO) ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 bg-slate-50 border-t border-slate-200" data-id="element-18036">
          <div className="max-w-5xl mx-auto" data-id="element-18037">
            <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-18038">
              <TargetIcon className="w-5 h-5 text-emerald-500" data-id="element-18039" />
              Жизненные сферы
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-18040">
              {Object.values(COMBO_NATAL_LIFE_AREAS).map((area: {
              title: string;
              icon: string;
              text: string;
            }, i: number) => <motion.div key={i} initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              delay: i * 0.05
            }} className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm" data-id="element-18041">
                    <h4 className="text-sm font-bold text-slate-900 mb-2" data-id="element-18042">
                      {area.title}
                    </h4>
                    <p className="text-xs text-slate-600 leading-relaxed" data-id="element-18043">
                      {area.text}
                    </p>
                  </motion.div>)}
            </div>
          </div>
        </div>

        {/* ══════════ 10. СОЛЯР — ВВЕДЕНИЕ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 bg-white border-t-4 border-amber-400" data-id="element-18044">
          <div className="max-w-5xl mx-auto text-center" data-id="element-18045">
            <Badge variant="warning" size="sm" className="mb-3" data-id="element-18046">
              Часть 2
            </Badge>
            <h2 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-18047">
              Солярный возврат
            </h2>
            <p className="text-sm text-slate-500 max-w-2xl mx-auto" data-id="element-18048">
              {COMBO_SOLAR_INTRO}
            </p>
          </div>
        </div>

        {/* ══════════ 11. ЭНЕРГИЯ ГОДА ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 bg-slate-50 border-t border-slate-200" data-id="element-18049">
          <div className="max-w-5xl mx-auto" data-id="element-18050">
            <h3 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2" data-id="element-18051">
              <SunIcon className="w-5 h-5 text-amber-500" data-id="element-18052" />
              Энергия года
            </h3>
            <div className="bg-white rounded-xl p-5 border border-slate-200" data-id="element-18053">
              <p className="text-sm text-slate-700 leading-relaxed" data-id="element-18054">
                {COMBO_YEAR_ENERGY}
              </p>
            </div>
          </div>
        </div>

        {/* ══════════ 12. СОЛЯР — АСЦЕНДЕНТ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 bg-white border-t border-slate-200" data-id="element-18055">
          <div className="max-w-5xl mx-auto" data-id="element-18056">
            <h3 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2" data-id="element-18057">
              <CompassIcon className="w-5 h-5 text-indigo-500" data-id="element-18058" />
              Асцендент соляра
            </h3>
            <div className="bg-indigo-50 rounded-xl p-5 border border-indigo-100" data-id="element-18059">
              <p className="text-sm text-slate-700 leading-relaxed" data-id="element-18060">
                {COMBO_SOLAR_ASCENDANT}
              </p>
            </div>
          </div>
        </div>

        {/* ══════════ 13. СОЛЯР — СОЛНЦЕ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 bg-slate-50 border-t border-slate-200" data-id="element-18061">
          <div className="max-w-5xl mx-auto" data-id="element-18062">
            <h3 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2" data-id="element-18063">
              <SunIcon className="w-5 h-5 text-amber-500" data-id="element-18064" />
              Солнце в соляре
            </h3>
            <div className="bg-white rounded-xl p-5 border border-slate-200" data-id="element-18065">
              <p className="text-sm text-slate-700 leading-relaxed" data-id="element-18066">
                {COMBO_SOLAR_SUN}
              </p>
            </div>
          </div>
        </div>

        {/* ══════════ 14. СОЛЯР — ЛУНА ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 bg-white border-t border-slate-200" data-id="element-18067">
          <div className="max-w-5xl mx-auto" data-id="element-18068">
            <h3 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2" data-id="element-18069">
              <MoonIcon className="w-5 h-5 text-blue-500" data-id="element-18070" />
              Луна в соляре
            </h3>
            <div className="bg-blue-50 rounded-xl p-5 border border-blue-100" data-id="element-18071">
              <p className="text-sm text-slate-700 leading-relaxed" data-id="element-18072">
                {COMBO_SOLAR_MOON}
              </p>
            </div>
          </div>
        </div>

        {/* ══════════ 15. СОЛЯР — ПЛАНЕТЫ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 bg-slate-50 border-t border-slate-200" data-id="element-18073">
          <div className="max-w-5xl mx-auto" data-id="element-18074">
            <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-18075">
              <StarIcon className="w-5 h-5 text-purple-500" data-id="element-18076" />
              Планеты в соляре
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-18077">
              {COMBO_SOLAR_PLANETS.map((planet, i) => <motion.div key={i} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: i * 0.04
            }} className="bg-white rounded-xl p-4 border border-slate-200" data-id="element-18078">
                  <h4 className="text-sm font-bold text-slate-900 mb-1" data-id="element-18079">
                    {planet.planet}
                  </h4>
                  <p className="text-[10px] text-slate-400 mb-2" data-id="element-18080">
                    {planet.position}
                  </p>
                  <p className="text-xs text-slate-600" data-id="element-18081">{planet.text}</p>
                </motion.div>)}
            </div>
          </div>
        </div>

        {/* ══════════ 16. СОЛЯР — АСПЕКТЫ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 bg-white border-t border-slate-200" data-id="element-18082">
          <div className="max-w-5xl mx-auto" data-id="element-18083">
            <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-18084">
              <ActivityIcon className="w-5 h-5 text-rose-500" data-id="element-18085" />
              Аспекты соляра
            </h3>
            <div className="space-y-3" data-id="element-18086">
              {COMBO_SOLAR_ASPECTS.map((aspect, i) => <motion.div key={i} initial={{
              opacity: 0,
              x: -10
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: i * 0.03
            }} className="bg-slate-50 rounded-xl p-4 border border-slate-200" data-id="element-18087">
                  <div className="flex items-center gap-2 mb-1" data-id="element-18088">
                    <span className="text-sm font-bold text-slate-900" data-id="element-18089">
                      {aspect.aspect}
                    </span>
                    <Badge variant="neutral" size="sm" className="text-[10px]" data-id="element-18090">
                      {aspect.type}
                    </Badge>
                  </div>
                  <p className="text-xs text-slate-600" data-id="element-18091">{aspect.text}</p>
                </motion.div>)}
            </div>
          </div>
        </div>

        {/* ══════════ 17. КЛЮЧЕВЫЕ ПЕРИОДЫ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 bg-slate-50 border-t border-slate-200" data-id="element-18092">
          <div className="max-w-5xl mx-auto" data-id="element-18093">
            <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-18094">
              <CalendarIcon className="w-5 h-5 text-emerald-500" data-id="element-18095" />
              Ключевые периоды года
            </h3>
            <div className="space-y-4" data-id="element-18096">
              {Object.entries(COMBO_KEY_PERIODS).map(([category, periods]: [string, {
              period: string;
              description: string;
            }[]]) => <div key={category} data-id="element-18097">
                    <h4 className="text-sm font-bold text-slate-700 mb-2 capitalize" data-id="element-18098">
                      {category === 'favorable' ? '🟢 Благоприятные' : category === 'tense' ? '🔴 Напряжённые' : '⚡ Время действовать'}
                    </h4>
                    <div className="space-y-2" data-id="element-18099">
                      {periods.map((period, i) => <motion.div key={i} initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: i * 0.05
                }} className="bg-white rounded-xl p-4 border border-slate-200 flex gap-4" data-id="element-18100">
                          <div className="w-20 shrink-0 text-center" data-id="element-18101">
                            <div className="text-xs font-bold text-indigo-600 bg-indigo-50 rounded-lg px-2 py-1" data-id="element-18102">
                              {period.period}
                            </div>
                          </div>
                          <div data-id="element-18103">
                            <p className="text-xs text-slate-600" data-id="element-18104">
                              {period.description}
                            </p>
                          </div>
                        </motion.div>)}
                    </div>
                  </div>)}
            </div>
          </div>
        </div>

        {/* ══════════ 18. ВЗАИМОДЕЙСТВИЕ СОЛЯР + НАТАЛ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 bg-white border-t border-slate-200" data-id="element-18105">
          <div className="max-w-5xl mx-auto" data-id="element-18106">
            <h3 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2" data-id="element-18107">
              <LayersIcon className="w-5 h-5 text-purple-500" data-id="element-18108" />
              Взаимодействие соляра и натала
            </h3>
            <div className="bg-purple-50 rounded-xl p-5 border border-purple-100" data-id="element-18109">
              <h4 className="font-bold text-slate-900 mb-2" data-id="element-18110">
                {COMBO_SOLAR_NATAL_INTERACTION.title}
              </h4>
              <p className="text-sm text-slate-700 leading-relaxed mb-3" data-id="element-18111">
                {COMBO_SOLAR_NATAL_INTERACTION.description}
              </p>
              <div className="space-y-2 mb-3" data-id="element-18112">
                {COMBO_SOLAR_NATAL_INTERACTION.activatedThemes.map((item: {
                theme: string;
                activation: string;
              }, i: number) => <div key={i} className="bg-white/60 rounded-lg p-3 border border-purple-100" data-id="element-18113">
                      <div className="text-sm font-semibold text-purple-800 mb-1" data-id="element-18114">
                        {item.theme}
                      </div>
                      <p className="text-xs text-slate-600" data-id="element-18115">
                        {item.activation}
                      </p>
                    </div>)}
              </div>
              <p className="text-sm text-slate-700 leading-relaxed" data-id="element-18116">
                {COMBO_SOLAR_NATAL_INTERACTION.importantEvents}
              </p>
            </div>
          </div>
        </div>

        {/* ══════════ ИТОГОВЫЙ АНАЛИЗ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 bg-gradient-to-b from-indigo-50 to-white border-t border-indigo-100" data-id="element-18117">
          <div className="max-w-5xl mx-auto text-center" data-id="element-18118">
            <SparklesIcon className="w-8 h-8 text-indigo-500 mx-auto mb-3" data-id="element-18119" />
            <h2 className="text-xl font-bold text-slate-900 mb-4" data-id="element-18120">
              Итоговый анализ
            </h2>
            <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm text-left" data-id="element-18121">
              <p className="text-sm text-slate-700 leading-relaxed" data-id="element-18122">
                Ваш комбо-анализ объединяет натальную карту и солярный возврат,
                создавая целостную картину вашей личности и предстоящего года.
                Используйте эти знания как компас для принятия решений и
                раскрытия своего потенциала.
              </p>
            </div>
          </div>
        </div>

        {/* ══════════ FOOTER ══════════ */}
        <div className="p-4 sm:p-6 bg-slate-50 border-t border-slate-200" data-id="element-18123">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 max-w-5xl mx-auto" data-id="element-18124">
            <div className="text-center sm:text-left" data-id="element-18125">
              <p className="text-sm font-medium text-slate-700" data-id="element-18126">
                Astriqo • Premium отчёт
              </p>
              <p className="text-xs text-slate-500 mt-1" data-id="element-18127">
                Отчёт сгенерирован {new Date().toLocaleDateString('ru-RU')} •
                Все права защищены
              </p>
            </div>
            <button onClick={handlePrint} className="report-print-btn flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-slate-200 hover:bg-slate-100 text-slate-600 hover:text-slate-800 transition-colors text-sm font-medium" data-id="element-18128">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-18129">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" data-id="element-18130" />
                <polyline points="7 10 12 15 17 10" data-id="element-18131" />
                <line x1="12" y1="15" x2="12" y2="3" data-id="element-18132" />
              </svg>
              Скачать PDF
            </button>
          </div>
        </div>

        {/* ══════════ CTA BLOCK ══════════ */}
        <div className="p-6 sm:p-8 md:p-10 bg-white border-t border-slate-200 text-center print:hidden" data-id="element-18133">
          <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-3" data-id="element-18134">
            Хотите персональный разбор от профессионала?
          </h3>
          <p className="text-sm sm:text-base text-slate-500 mb-6 max-w-lg mx-auto" data-id="element-18135">
            Закажите индивидуальную консультацию с опытным астрологом для
            глубокого анализа вашей карты
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3" data-id="element-18136">
            <button className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-sm font-medium transition-colors" data-id="element-18137">
              <UsersIcon className="w-4 h-4" data-id="element-18138" />
              Каталог астрологов
            </button>
            <button className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium transition-colors shadow-sm" data-id="element-18139">
              Заказать консультацию
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-18140">
                <line x1="5" y1="12" x2="19" y2="12" data-id="element-18141" />
                <polyline points="12 5 19 12 12 19" data-id="element-18142" />
              </svg>
            </button>
          </div>
        </div>

        {/* ══════════ REVIEW SECTION ══════════ */}
        <div className="p-6 sm:p-8 md:p-10 bg-slate-50 border-t border-slate-200 print:hidden" data-id="element-18143">
          <div className="max-w-lg mx-auto text-center" data-id="element-18144">
            {!reviewSubmitted ? <div data-id="element-18145">
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4" data-id="element-18146">
                  <SparklesIcon className="w-6 h-6 text-amber-500" data-id="element-18147" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-1" data-id="element-18148">
                  Как вам отчёт?
                </h3>
                <p className="text-sm text-slate-500 mb-6" data-id="element-18149">
                  Ваш отзыв поможет улучшить качество наших отчётов
                </p>

                {/* Star Rating */}
                <div className="flex items-center justify-center gap-2 mb-6" data-id="element-18150">
                  {[1, 2, 3, 4, 5].map(star => <button key={star} onClick={() => setReviewRating(star)} className="transition-transform hover:scale-110" data-id="element-18151">
                      <StarIcon className={`w-8 h-8 transition-colors ${star <= reviewRating ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} data-id="element-18152" />
                    </button>)}
                </div>

                {reviewRating > 0 && <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} className="space-y-4" data-id="element-18153">
                    {/* Quick Tags */}
                    <div className="flex flex-wrap justify-center gap-2" data-id="element-18154">
                      {['Точность', 'Глубина', 'Оформление', 'Практичность', 'Персонализация', 'Читаемость'].map(tag => <button key={tag} onClick={() => setReviewTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag])} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${reviewTags.includes(tag) ? 'bg-indigo-100 text-indigo-700 border border-indigo-200' : 'bg-white text-slate-600 border border-slate-200 hover:border-slate-300'}`} data-id="element-18155">
                          {tag}
                        </button>)}
                    </div>

                    {/* Text Area */}
                    <textarea value={reviewText} onChange={e => setReviewText(e.target.value)} placeholder="Расскажите подробнее (необязательно)" rows={3} className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-indigo-400 resize-none" data-id="element-18156" />

                    {/* Submit */}
                    <button onClick={() => setReviewSubmitted(true)} className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-medium transition-colors shadow-sm" data-id="element-18157">
                      Отправить отзыв
                    </button>

                    <p className="text-xs text-slate-400 mt-2" data-id="element-18158">
                      💎 Оставьте отзыв и получите скидку 10% на следующий отчёт
                    </p>
                  </motion.div>}
              </div> : <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} data-id="element-18159">
                <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4" data-id="element-18160">
                  <CheckIcon className="w-6 h-6 text-emerald-600" data-id="element-18161" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-1" data-id="element-18162">
                  Спасибо за ваш отзыв!
                </h3>
                <p className="text-sm text-slate-500 mb-4" data-id="element-18163">
                  Скидка 10% уже применена к вашему аккаунту
                </p>
                <div className="flex items-center justify-center gap-1" data-id="element-18164">
                  {[1, 2, 3, 4, 5].map(star => <StarIcon key={star} className={`w-5 h-5 ${star <= reviewRating ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} data-id="element-18165" />)}
                </div>
              </motion.div>}
          </div>
        </div>
      </>;
  };
  // ═══════════════════════════════════════════
  // MAIN RENDER
  // ═══════════════════════════════════════════
  switch (serviceType) {
    case 'natal':
      return renderNatalContent();
    case 'solar':
      return renderSolarContent();
    case 'compatibility':
      return renderCompatibilityContent();
    case 'combo':
      return renderComboContent();
    case 'forecast':
      return renderSolarContent();
    default:
      return renderNatalContent();
  }
}